/**
 * Word frontend tool handler registry.
 *
 * Each key is the `frontend_binding.handler` value from the Cosmos DB tool spec.
 * The add-in dispatches incoming `frontend_tool_call` SSE events by this key.
 *
 * Adding a new tool = add one entry here + one Cosmos document under
 * `integrations/frontend_tools/word.json` with a matching handler string.
 */

import {
  replaceWordSelection,
  insertAtWordCursor,
  appendToWordEnd,
  applyWordReview,
  executeReviewEdits,
  type ReviewEditFinding,
  type ReviewEditResult,
  type ReviewSkip,
  applyWordStyles,
  getWordFormatCandidates,
  type WordStyleChange,
  insertWordComment,
  insertWordComments,
  type BatchCommentResult,
  type CommentInsertResult,
  getWordSelection,
  getWordSelectionContext,
  readWordParagraphs,
  replaceAllInDocument,
  highlightWordText,
  clearWordHighlights,
  claimSessionHighlights,
  currentHighlightTurn,
  realignTurnHighlights,
  getWordTableOfContents,
  readWordDocumentMap,
  type TocEntry,
  outlineFromToc,
  type OutlineRow,
  outlineToMarkdown,
  outlineRowsToSections,
  outlineSectionsToMarkdown,
  getWordReviewItems,
  insertWordTable,
  insertWordTOC,
  insertWordOutline,
  type OutlineSectionInput,
  insertWordPageNumbers,
  removeWordPageNumbers,
  hasWordPageNumbers,
  surveyWordRegions,
  supportsFieldInsertion,
  setWordChangeTracking,
  insertWordEmailAtCursor,
  stripAllWordStyles,
  countWordSelectionParagraphs,
  stripSelectionStyles,
  surveyWordFormatting,
  getWordComments,
  getAnchoredWordComments,
  type AnchoredWordComment,
  resolveWordComment,
  deleteWordComment,
  countWordTrackedChanges,
  applyWordTrackedChanges,
  normaliseUrl,
  insertWordHyperlink,
  insertWordBates,
  removeWordBates,
  normaliseBatesConfig,
  formatBatesStamp,
  describeBatesPlacement,
  extractBatesStamps,
  getWordOccurrences,
  getWordTrackingEnabled,
  buildWordClipboardHtml,
  normalisePageNumberStart,
  PAGE_NUMBER_START_MIN,
  type PageNumberAlignment,
  type PageNumberFormat,
  type PageNumberPosition,
  type PageNumberNumeralStyle,
  type RegionSurvey,
} from "./services/wordDocument";
import {
  changeSnippet,
  getSessionStartMs,
  recordWordChange,
  setTrackingStateReader,
  textPresenceVerifier,
  unreportedReviewItems,
  type ChangeVerifier,
} from "./services/changeLog";
import {
  anchorFindings,
  cleanupAllAnchors,
  isDocumentReadOnly,
  findMissingHyperlink,
  findBrokenReference,
  findBracketHeadingMismatch,
  findTopicMismatchData,
  findInconsistentFormatting,
  findExhibitScheduleMismatch,
  findDefinedTermSectionMismatch,
  definedTermSectionNotes,
  findHyperlinkTargetMismatchData,
  findShortenedHyperlinkText,
  findDefinedNotUsed,
  findUsedNotDefinedCandidates,
  findAlphabeticalOrder,
  findMissingDefinedTerm,
  findUsedBeforeDefined,
  readAnchorState,
  selectFindingLocation,
  repairFindingAnchor,
  fixMissingHyperlink,
  fixBrokenReference,
  fixBracketHeadingMismatch,
  fixTopicMismatch,
  fixInconsistentFormatting,
  fixExhibitScheduleMismatch,
  fixExhibitScheduleMismatchFlag,
  fixAmbiguousReference,
  fixDefinedTermSectionMismatch,
  fixHyperlinkTargetMismatch,
  fixHyperlinkTargetMismatchFlag,
  fixShortenedHyperlinkText,
  fixShortenedHyperlinkTextFlag,
  fixDefinedNotUsed,
  fixUsedNotDefined,
  fixNumberingOrder,
  fixNumberingOrderRelist,
  fixNumberingOrderHeadingAnomalyFlag,
  fixAlphabeticalOrder,
  fixMissingDefinedTerm,
  fixUsedBeforeDefined,
  // AIVA-7506 frontend rework: the 10 rules that need no LLM judgment
  // evaluate here, right after their own find<Rule> sweep — see
  // docReview.ts's "Shared deterministic-evaluation helpers" section.
  evaluateMissingHyperlink,
  evaluateBrokenReference,
  evaluateBracketHeadingMismatch,
  evaluateInconsistentFormatting,
  evaluateExhibitScheduleMismatch,
  evaluateShortenedHyperlinkText,
  evaluateDefinedNotUsed,
  evaluateAlphabeticalOrder,
  parseDefinitionSortKey,
  acronymsWithoutFullForm,
  evaluateNumberingOrder,
  evaluateNumberingOrderHeadingAnomalies,
  evaluateMissingDefinedTerm,
  evaluateUsedBeforeDefined,
  evaluateAmbiguousReference,
  // The 4 rules that still need an LLM call — these narrow the raw sweep to
  // just the judgment-relevant subset, mirroring the pre-filter/bucketing
  // logic that used to live server-side in rules.py/judgment.py.
  filterTopicMismatchJudgmentCandidates,
  filterHyperlinkTargetMismatchJudgmentTargets,
  findExhibitScheduleAmbiguousMentions,
  findExhibitScheduleMeaningCandidates,
  type MissingHyperlinkSweep,
  type BrokenReferenceSweep,
  type BracketHeadingMismatchSweep,
  type TopicMismatchSweep,
  type InconsistentFormattingSweep,
  type ExhibitScheduleMismatchSweep,
  type DefinedTermSectionMismatchSweep,
  type HyperlinkTargetMismatchSweep,
  type ShortenedHyperlinkTextSweep,
  type DefinedNotUsedSweep,
  type UsedNotDefinedSweep,
  type AlphabeticalOrderSweep,
  type DefinitionSortKey,
  type MissingDefinedTermSweep,
  type UsedBeforeDefinedSweep,
} from "./services/docReview";
import { profileSweep, scanProfilingEnabled } from "./services/scanProfiler";
import { bulkCreateFindings, reconcileFindings, type FindingCreate } from "../../../src/docReview/services/api";
import { fetchEmailInlineImages } from "../../../src/chat/services/emailImagesApi";
import { setScanNotes } from "../../../src/docReview/scanNotes";
// HandlerContext is declared alongside Deckorate's own handlers (the first
// caller to need per-call auth context) but is the shared type every
// channel's HandlerFn accepts — imported directly from there rather than
// through ../index to avoid a circular import (../index -> ./index -> here).
import type { HandlerContext } from "../deckorate/handlers";
import {
  outlineAwareLabel,
  parseOutlineTextToSections,
  stripInventedSectionHeading,
} from "./utils/parseWordResponse";
import {
  WORD_TEMPLATE_SKELETONS,
  countTemplateSections,
  isTemplateKey,
} from "./data/templateSkeletons";
import { emitPills } from "../outlook/pill-bridge";
import { emitToast } from "../outlook/toast-bridge";
import { execCommandCopyFallback } from "../clipboard";

export type HandlerFn = (args: Record<string, unknown>) => Promise<unknown>;

const _s = (v: unknown): string => (typeof v === "string" ? v : "");
const INSERTED = { inserted: true } as const;

// AIVA-8048: every edit this module records is stamped with whether Word's
// Track Changes was on when it was made, so /stop-tracking reports only the
// edits made while tracking — see setTrackingStateReader.
setTrackingStateReader(getWordTrackingEnabled);

/**
 * A text edit's verifier, with how many copies of its text the document held
 * BEFORE the edit (AIVA-8046). Call it before writing. Those copies were
 * already there, so they must not make the edit look present once the user
 * undoes it — verifyWordChangeLog counts only the copies beyond them.
 * Best-effort: an unreadable count leaves the verifier as it was.
 */
async function withBaseline(verifier: ChangeVerifier | undefined): Promise<ChangeVerifier | undefined> {
  if (verifier?.kind !== "textPresent") return verifier;
  try {
    const occ = await getWordOccurrences(verifier.text, 0);
    return typeof occ?.count === "number" ? { ...verifier, baseline: occ.count } : verifier;
  } catch {
    return verifier;
  }
}

/** A Graph ISO timestamp as a bare "14:22", or "" when it cannot be read.
 *
 *  Used to separate paste-email candidates that a DAY cannot separate
 *  (AIVA-7956). Rendered in the viewer's own locale via the host's timezone,
 *  matching how the user sees the same message in Outlook; an unparseable value
 *  yields "" so the label simply falls back to the day rather than showing
 *  "Invalid Date" to the user. */
function formatEmailTime(iso: string): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  try {
    return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** Resolve a TO-01 template key to its frame.
 *
 *  Shared by `ui.pill.word.insert_template` (which labels the pill) and
 *  `word.insert_template` (which writes it). Single lookup on purpose: the pill
 *  promises "NDA template (10)" and the handler must deliver exactly that frame
 *  with exactly that many sections. Two copies of the resolve could drift, and
 *  the user would only find out after clicking.
 *
 *  @param caller tool name, so an unknown key names the tool that sent it.
 */
function resolveTemplate(template: unknown, caller: string) {
  const key = _s(template);
  if (!isTemplateKey(key)) throw new Error(`${caller}: unknown template "${key}"`);
  const { label, markdown } = WORD_TEMPLATE_SKELETONS[key];
  return { key, label, markdown, sections: countTemplateSections(markdown) };
}

// `outlineSectionsToMarkdown` used to live here as a private copy. It moved to
// wordDocument.ts so the render layer can synthesise the Copy pill from the same
// function — the clipboard string and the inserted document must not be able to
// drift apart. Its reasoning, unchanged: the heading MARKERS are what
// `markdownToClipboardHtml` turns into real <h1>/<h2>/<h3>, so a paste keeps its
// structure. HTML has no equivalent of Word's "Title" style, so a title section
// maps to `#` alongside level 1 — a limit of the format, not a choice.

/** Emit one text-payload office_action pill. Shared by the three near-identical
 *  `ui.pill.word.{replace_selection,insert_at_cursor,append_to_end}` handlers —
 *  their validation + emit shape is identical, so keep the pill dictionary here
 *  instead of copying it three times (Sonar duplication). */
/**
 * Does this call carry Bates-numbering configuration?
 *
 * Bates numbering is a DIFFERENT capability — a prefix, zero padding and a start
 * offset, with litigation-production semantics — and AIVA does not implement it.
 * That makes this guard more important, not less: servicing such a request as
 * plain page numbers would stamp the wrong thing into a court bundle, so the
 * page-number handler refuses outright rather than approximating. Checked on the args
 * (not on handler ordering) because this codebase routes by skill, not by a
 * dispatch chain — the ordering guard the specification relies on doesn't exist
 * here, so the guard has to live on the data.
 */
export function hasBatesIntent(args: Record<string, unknown> | undefined): boolean {
  if (!args) return false;
  // `start_at` is deliberately NOT in this list any more (AIVA-7867).
  //
  // A start offset on its own is not Bates — it is Word's own "Format Page
  // Numbers -> Start at", an ordinary request ("number this from 500") that
  // /pagenumbers now services. Treating it as Bates intent meant the moment
  // start_at became expressible, every custom-start request was answered with a
  // refusal about court bundles. What makes a request Bates is the PRODUCTION
  // STAMP — a prefix or zero padding — or the user saying the word.
  if (args.prefix !== undefined || args.padding !== undefined) return true;
  return Object.entries(args).some(
    ([key, v]) =>
      // impact_line is OUR OWN prose, added by the pill emitter, and it says
      // "looks like a Bates stamp" when the footer already holds one. Scanning
      // it made the guard fire on the user's approved click and refuse a
      // page-number request nobody had made. Only model/user-supplied fields
      // can express intent.
      key !== "impact_line" && typeof v === "string" && /\bbates\b/i.test(v),
  );
}

/**
 * Read the requested page-number numeral style off the tool args (AIVA-7863).
 *
 * Shared by the pill emitter and the write handler so the button's label can
 * never promise a numeral the write does not deliver — which is the whole
 * complaint in the ticket: AIVA said "Roman numerals" and stamped 1, 2, 3.
 * Anything unrecognised falls back to Arabic, matching how `format` and
 * `position` already narrow an unknown value to their default.
 */
function pageNumeralStyle(args: Record<string, unknown> | undefined): PageNumberNumeralStyle {
  const raw = _s(args?.numeral_style).trim().toLowerCase();
  if (raw === "roman_upper") return "roman_upper";
  if (raw === "roman_lower") return "roman_lower";
  if (raw === "alpha_upper") return "alpha_upper";
  if (raw === "alpha_lower") return "alpha_lower";
  // The bare word the USER says, not the enum value. The schema and the skill
  // both name alpha_lower, but this reaches us from a model paraphrasing the
  // user's own "alphabetic numbering" — and an unmapped value here does not
  // fail loudly, it silently becomes arabic and stamps 1, 2, 3, which IS the
  // defect in AIVA-7866. Same reasoning as readPageNumberAlignment accepting
  // "centre"/"centered". Lower case is the unqualified default, per the skill.
  if (raw === "alphabetic" || raw === "alphabetical") return "alpha_lower";
  return "arabic";
}

/**
 * Read the requested first-page number off the tool args (AIVA-7867).
 *
 * Shared by the pill emitter and the write handler for exactly the reason the
 * numeral style is: the click is a separate turn from the one that chose the
 * value, so a start the button promises has to be the start the write receives.
 *
 * Three spellings are accepted because this arrives from a model paraphrasing
 * the user, not from a form. `start_at` is what the schema names; `start_number`
 * and `startAt` are what models reach for anyway, and an unrecognised key here
 * does not fail loudly — it silently numbers from 1, which IS the defect in this
 * ticket. Same reasoning as pageNumeralStyle accepting a bare "alphabetic".
 */
function pageNumberStartAt(args: Record<string, unknown> | undefined): number {
  const raw = args?.start_at ?? args?.start_number ?? args?.startAt;
  if (raw === undefined || raw === null || raw === "") return PAGE_NUMBER_START_MIN;
  return normalisePageNumberStart(raw);
}

/** `" starting at 500"`, or nothing at all when the run starts at 1. */
function pageNumberStartNote(startAt: number): string {
  return startAt > PAGE_NUMBER_START_MIN ? ` starting at ${startAt}` : "";
}

/**
 * Keys that mean "leave the contents pages unnumbered" (AIVA-7869).
 *
 * Five spellings for the same reason `pageNumberStartAt` accepts three: this
 * arrives from a model paraphrasing the user, not from a form, and an
 * unrecognised key here does not fail loudly — it numbers the contents pages
 * too, which IS the defect in this ticket.
 */
const SKIP_FRONT_MATTER_KEYS = [
  "skip_front_matter",
  "skipFrontMatter",
  "exclude_front_matter",
  "skip_toc",
  "exclude_toc",
] as const;

/** Did the caller ask for the front matter to be left unnumbered? */
export function readSkipFrontMatter(args: Record<string, unknown> | undefined): boolean {
  for (const key of SKIP_FRONT_MATTER_KEYS) {
    const raw = args?.[key];
    if (raw === undefined || raw === null || raw === "") continue;
    if (typeof raw === "boolean") return raw;
    // Only an explicit negative reads as false. Anything else a model puts here
    // was put there because the user asked for the skip.
    const text = (typeof raw === "number" ? String(raw) : _s(raw)).trim().toLowerCase();
    return !(text === "false" || text === "no" || text === "0" || text === "off");
  }
  return false;
}

/**
 * What a skip-the-front-matter run can actually deliver, and what it had to
 * drop to do it.
 *
 * ONE resolver for both the pill emitter and the write handler, because the
 * click is a separate turn from the one that chose the options: anything the
 * two could compute differently is a label that promises what the write does
 * not deliver, which is the whole history of this feature (AIVA-7862, -7863,
 * -7866, -7867).
 *
 * `overridden` is the honest half. Blanking the contents pages rests on a
 * numeric picture switch that only understands Arabic digits, so `with_total`
 * and a Roman/alphabetic numeral genuinely cannot ride along — see
 * `pageNumberSkipFrontMatterOoxml`. The caller states every one it dropped
 * rather than quietly ignoring it.
 *
 * A custom start is NOT dropped any more: the skip formula carries the offset
 * conditionally, so "start at 200 excluding the contents" is now one request
 * rather than two that cancel.
 */
interface FrontMatterPlan {
  readonly skipFrontMatter: boolean;
  readonly format: PageNumberFormat;
  readonly numeralStyle: PageNumberNumeralStyle;
  readonly startAt: number;
  readonly overridden: readonly string[];
}

function planFrontMatterSkip(
  args: Record<string, unknown> | undefined,
  format: PageNumberFormat,
  numeralStyle: PageNumberNumeralStyle,
  startAt: number,
): FrontMatterPlan {
  const skipFrontMatter = readSkipFrontMatter(args);
  if (!skipFrontMatter) {
    return { skipFrontMatter: false, format, numeralStyle, startAt, overridden: [] };
  }
  const overridden: string[] = [];
  if (format === "with_total") overridden.push("the page X of Y format");
  if (numeralStyle !== "arabic") overridden.push(numeralStyleWording(numeralStyle));
  return {
    skipFrontMatter: true,
    format: "simple",
    numeralStyle: "arabic",
    // Carried, not reset: the skip package offsets the body and still blanks
    // the front matter.
    startAt,
    overridden,
  };
}

/** `" (contents pages blank)"` — appended to the approval button's label. */
function frontMatterLabelSuffix(plan: FrontMatterPlan): string {
  return plan.skipFrontMatter ? " (contents pages blank)" : "";
}

/**
 * The sentence that says what a skip run does, plus every option it dropped.
 *
 * Shared by the approval card and the post-write message so the user reads the
 * same promise before and after the click.
 */
function frontMatterNote(plan: FrontMatterPlan): string {
  if (!plan.skipFrontMatter) return "";
  const overriddenVerb = plan.overridden.length === 1 ? "is" : "are";
  const dropped = plan.overridden.length
    ? ` Plain 1, 2, 3 numbering is the only form that can leave a page blank, so ${listPhrase(plan.overridden)} ${overriddenVerb} not applied.`
    : "";
  // "the first page after them", not "the first page of the body": when the
  // body starts on the same page the contents ends, that page counts as front
  // matter and stays blank, so numbering begins on the page after it. Promising
  // the body's first page would be wrong on exactly the documents that need it.
  //
  // The start is named here rather than left to `pageNumberStartNote`: that
  // note reads "starting at 200", which on a skip run would otherwise sound
  // like the CONTENTS starts at 200.
  return (
    ` The contents pages stay blank and numbering starts at ${plan.startAt} on the first page` +
    ` after them.${dropped}`
  );
}

/** `"a, b and c"` — for naming the options a skip run had to drop. */
function listPhrase(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** `"3 sections"` / `"1 section"` — shared by the write handler and its
 *  failure prose, so the two cannot disagree about the wording. */
function sectionCount(n: number): string {
  return `${n} section${n === 1 ? "" : "s"}`;
}


/**
 * Why a skip-the-front-matter run wrote nothing (AIVA-7869).
 *
 * Every branch leaves the document unchanged and says what to do next. None of
 * them falls back to numbering the whole document: a contents page that comes
 * back numbered is indistinguishable, to the user, from the request being
 * ignored — which is the ticket.
 */
function frontMatterFailureMessage(reason: string | undefined): string | undefined {
  if (reason === "no_front_matter") {
    return (
      "I couldn't find a contents page in this document, so there was nothing to leave out and I've made no " +
      "changes. Ask me to add page numbers and I'll number every page — or insert a table of contents first, " +
      "then ask again."
    );
  }
  if (reason === "toc_not_rendered") {
    return (
      "This document has a table-of-contents field that Word hasn't calculated yet, so I can't tell where the " +
      "contents ends — and I won't guess and leave the wrong pages blank. Refresh it first (click in the " +
      "contents and press F9, or open Print Preview), then ask me again."
    );
  }
  if (reason === "no_body_after_front_matter") {
    return (
      "The contents is the last thing in this document, so excluding it would leave nothing to number. " +
      "The document is unchanged."
    );
  }
  return undefined;
}

function pageNumberFailureMessage(
  result: { reason?: string; skipped?: number; errorDetail?: string },
  position: PageNumberPosition,
  startAt: number,
  skipFrontMatter = false,
): string {
  const frontMatter = frontMatterFailureMessage(result.reason);
  if (frontMatter) return frontMatter;
  if (result.reason === "offset_unsupported" && skipFrontMatter) {
    return (
      `This version of Word wouldn't accept the calculated field that leaving the contents pages blank needs, ` +
      `so I've left the document unchanged rather than numbering the contents too and letting it look like ` +
      `your request was applied. In Word you can do it by hand: put a section break after the contents, ` +
      `unlink that section's ${position}, then Insert → Page Number → Format Page Numbers → Start at 1.`
    );
  }
  if (result.reason === "offset_unsupported") {
    return (
      `This version of Word wouldn't accept a page number starting at ${startAt} — it needs a calculated ` +
      `field that the host rejected — so I've left the document unchanged rather than numbering from 1 ` +
      `and letting it look like your request was applied. You can set it in Word directly: ` +
      `Insert → Page Number → Format Page Numbers → Start at.`
    );
  }
  if (result.skipped) {
    return (
      `The ${position} already has page numbers in ${sectionCount(result.skipped)}, so I've left the document ` +
      `unchanged rather than adding a second set. Ask me to remove them first if you want a different format.`
    );
  }
  const why = result.errorDetail ? ` Word reported: ${result.errorDetail}.` : "";
  return `I couldn't add page numbers to the ${position}. The document is unchanged.${why}`;
}

/**
 * How each numeral style is named and exemplified to the user. Both fields are
 * empty for arabic, which is the unremarkable default and needs no explaining.
 *
 * One table rather than a branch per call site, so the pill's label and the
 * confirmation prose cannot drift apart and describe different numerals — the
 * name and the example live together for the same reason, so a style can never
 * be called "Roman" while showing "a, b, c" (AIVA-7866).
 */
const NUMERAL_STYLE: Readonly<Record<PageNumberNumeralStyle, { name: string; example: string }>> = {
  arabic: { name: "", example: "" },
  roman_lower: { name: "lowercase Roman numerals", example: "i, ii, iii" },
  roman_upper: { name: "uppercase Roman numerals", example: "I, II, III" },
  alpha_lower: { name: "lowercase letters", example: "a, b, c" },
  alpha_upper: { name: "uppercase letters", example: "A, B, C" },
};

/** How to describe a numeral style to the user, or "" for plain Arabic. */
function numeralStyleWording(style: PageNumberNumeralStyle): string {
  const { name, example } = NUMERAL_STYLE[style];
  return name ? `${name} (${example})` : "";
}

/** `" in lowercase Roman numerals (i, ii, iii)"`, or "" for plain Arabic. */
function numeralStyleNote(style: PageNumberNumeralStyle): string {
  const wording = numeralStyleWording(style);
  return wording ? ` in ${wording}` : "";
}

/** `" (i, ii, iii)"` — appended to the approval button's label. */
function numeralStyleLabelSuffix(style: PageNumberNumeralStyle): string {
  const { example } = NUMERAL_STYLE[style];
  return example ? ` (${example})` : "";
}

/**
 * The confirmation prose shown on the page-number approval card.
 *
 * Extracted from the pill emitter so that handler stays a straight line — pick
 * the options, describe them, emit one pill — and stays under the cognitive
 * complexity limit (sonar typescript:S3776): the branching here is all about
 * WORDING, and none of it needs the emitter's scope.
 *
 * A null `survey` is a host that could not enumerate sections, which degrades to
 * the generic wording rather than claiming a count it does not have.
 *
 * `alignPhrase` and `numeralNote` are pre-rendered by the caller (both empty for
 * the centred-Arabic default) so this stays a pure string builder.
 */
function pageNumberImpactLine(
  survey: RegionSurvey | null,
  position: PageNumberPosition,
  alignPhrase: string,
  numeralNote: string,
): string {
  if (!survey) {
    return `Adds${alignPhrase} page numbers${numeralNote} to the ${position} of every section. Existing ${position} content is kept.`;
  }
  const s = survey.sectionCount;
  let line = `Adds${alignPhrase} page numbers${numeralNote} to the ${position} of ${s} section${s === 1 ? "" : "s"}.`;
  if (survey.occupied.length === 0) return line;

  const found = survey.occupied
    .slice(0, 3)
    .map((r) => `section ${r.section}: "${changeSnippet(r.text)}"`)
    .join("; ");
  line += ` The ${position} already contains content — ${found}. It will be kept; the page number is added below it.`;
  if (survey.anyBatesStamped) {
    // Never silently renumber over a Bates stamp (G-14).
    line += " One of those looks like a Bates stamp — check the numbering is still correct after applying.";
  }
  return line;
}

/**
 * Read the requested page-number alignment (AIVA-7862).
 *
 * Centre is the default — DP-03's AC-1 specifies a centred field, and it is what
 * every existing caller got — so anything unrecognised falls back to it rather
 * than failing the turn. Only an explicit left/right moves the number.
 *
 * Both spellings of "centre" are accepted, and so is "centered": the value
 * reaches here from a language model reading the user's own words, and refusing
 * a spelling would silently re-centre a request that was already centred anyway.
 */
export function readPageNumberAlignment(args: Record<string, unknown> | undefined): PageNumberAlignment {
  const raw = _s(args?.alignment).trim().toLowerCase();
  if (raw === "left") return "left";
  if (raw === "right") return "right";
  return "center";
}

/**
 * The prefix a Bates REMOVAL is scoped to — or undefined for "every prefix".
 *
 * Removal deliberately has no WL- default: normaliseBatesConfig's defaults
 * describe what to WRITE, and "remove bates numbering" after a cleared chat
 * must strip the SIM-/ABC- stamps actually in the document, not hunt for WL-.
 */
export function readBatesRemovalPrefix(args: Record<string, unknown> | undefined): string | undefined {
  const raw = _s(args?.prefix).trim();
  return raw || undefined;
}

/**
 * The region a removal is scoped to — or undefined when the request named none,
 * which means EVERY region.
 *
 * Shared by page-number and Bates removal, and it has to be: AIVA-7938 was a
 * page-number reader that said `=== "header" ? "header" : "footer"`, so an
 * omitted argument — the normal case for a bare "remove the page numbers" —
 * silently meant FOOTER. A document numbered in the header was swept in the
 * footer, matched nothing, and reported "There were no page numbers to remove
 * from the footer". Bates removal already read it correctly, and the fix was to
 * copy that reader — which left two byte-identical functions and a second place
 * for the same defect to come back to. There is now one.
 *
 * Naming a region still scopes to it; anything unrecognised WIDENS to every
 * region rather than quietly narrowing to one, which is how the original hid.
 */
export function readRemovalRegion(
  args: Record<string, unknown> | undefined,
): PageNumberPosition | undefined {
  const raw = _s(args?.position).trim().toLowerCase();
  if (raw === "header") return "header";
  if (raw === "footer") return "footer";
  return undefined;
}

/** The regions a removal will actually sweep. */
function removalRegions(position: PageNumberPosition | undefined): readonly PageNumberPosition[] {
  return position ? [position] : (["footer", "header"] as const);
}

/** How a region choice reads in the impact line and the result message. */
function regionLabel(position: PageNumberPosition | undefined): string {
  return position ?? "header and footer";
}

/** The same wording as `regionLabel`, from a list rather than a single choice.
 *  Both regions always read "header and footer" regardless of sweep order. */
function regionListLabel(regions: readonly PageNumberPosition[]): string {
  return regions.length === 2 ? "header and footer" : (regions[0] ?? "document");
}

/**
 * The sentence `word.check_page_numbers` reports, covering the three outcomes
 * that are not a plain "yes".
 *
 * A region whose read was REFUSED is never folded into the negative. "There are
 * no page numbers" and "I could not look" are different answers, and reporting
 * the second as the first is the same defect this tool exists to fix — just
 * sourced from a failed read instead of from the transcript.
 */
function checkPageNumbersMessage(
  locations: readonly PageNumberPosition[],
  checked: readonly PageNumberPosition[],
  unreadable: readonly PageNumberPosition[],
): string {
  if (locations.length > 0) {
    return `This document has page numbers in the ${regionListLabel(locations)}.`;
  }
  if (unreadable.length === checked.length) {
    return (
      `I couldn't read the ${regionListLabel(unreadable)} of this document, ` +
      `so I can't say whether it has page numbers.`
    );
  }
  if (unreadable.length > 0) {
    const read = checked.filter((r) => !unreadable.includes(r));
    return (
      `There are no page numbers in the ${regionListLabel(read)}. I couldn't read the ` +
      `${regionListLabel(unreadable)}, so I can't rule one out there.`
    );
  }
  return `This document has no page numbers in the ${regionListLabel(checked)}.`;
}

/**
 * Pre-read for the remove-Bates pill: the largest section count seen across
 * the regions the removal will sweep, and the stamps actually FOUND there
 * (deduped, prefix-scoped when one was given) so the impact line can name
 * which numbers are about to go. sectionCount is null when no region could
 * be surveyed — the caller degrades to "every section".
 */
async function surveyBatesRemovalTargets(
  regions: readonly PageNumberPosition[],
  prefix: string | undefined,
): Promise<{ sectionCount: number | null; stamps: string[] }> {
  let sectionCount: number | null = null;
  const found: string[] = [];
  for (const region of regions) {
    const survey = await surveyWordRegions(region); // NOSONAR - header and footer Word.run batches stay ordered
    if (!survey) continue;
    sectionCount = Math.max(sectionCount ?? 0, survey.sectionCount);
    for (const occupied of survey.occupied) {
      found.push(...extractBatesStamps(occupied.text, prefix));
    }
  }
  return { sectionCount, stamps: [...new Set(found)] };
}

/**
 * The longest leading slice of `text` that Word's search can look for.
 *
 * An echo is detectable from its first sentence, so the probe is one sentence,
 * capped well under Word's 255-character search limit. Short slices are dropped
 * rather than searched: "See below." would match half a document and the check
 * has to be precise enough that a real draft never trips it.
 */
function echoProbe(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  const sentenceEnd = flat.search(/[.!?]\s/);
  const slice = (sentenceEnd > 0 ? flat.slice(0, sentenceEnd + 1) : flat).slice(0, 180);
  // Eight words is comfortably longer than any boilerplate opener and far shorter
  // than a drafted clause, so it sits in the gap between the two.
  return slice.split(/\s+/).length >= 8 ? slice : "";
}

/**
 * Whether this reply is a paragraph the document already contains.
 *
 * AIVA-7938. `sameProse` below compares against the SELECTION, which catches
 * "/passive-style" echoes but not this one: asked to draft with nothing
 * selected, /write returned the document's own Business Process Automation
 * paragraph verbatim. There was no selection, so that check was inert.
 *
 * The channel here is the anchored window — /write reads ±30 paragraphs around
 * the located section as CONTEXT and handed one back. The prompt forbids that in
 * three places; this is the part that does not depend on the model agreeing.
 *
 * Best-effort by design. A host that will not search must not cost a real draft
 * its placement buttons, so an unreadable document skips the check.
 */
async function isEchoOfDocument(text: string): Promise<boolean> {
  const probe = echoProbe(text);
  if (!probe) return false;
  try {
    const found = await getWordOccurrences(probe, 1);
    return (found?.count ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Text compared the way a reader would: whitespace and case are not content. */
function sameProse(a: string, b: string): boolean {
  const norm = (v: string) =>
    v.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"').replace(/\s+/g, " ").trim().toLowerCase();
  const left = norm(a);
  return left.length > 0 && left === norm(b);
}

/**
 * Emit one text-placement pill, unless the text is the selection returned.
 *
 * AIVA-7937. `/write` was reported three times handing the selection back as
 * its "draft" — the last one a byte-identical diff, 0 removals and 0 additions.
 * The prompt forbids it in three places now, and a prompt rule is only as good
 * as the model's compliance, so the refusal also lives here where it cannot be
 * talked out of.
 *
 * A pill is worse than a bare wrong answer: `Replace selection` would paste the
 * user's own paragraphs over the user's own paragraphs — a no-op that still
 * costs an undo entry and marks the document dirty — and `Insert at cursor`
 * would duplicate them somewhere else.
 *
 * Not /write-specific on purpose. Every transformation skill shares this
 * emitter, and for all of them an unchanged reply means the model changed
 * nothing: /polish, /simplify, /formal and the rest are equally wrong to offer
 * a button for it. Reported as a SUCCESSFUL call with `rendered: false` plus a
 * `message`, which is the contract `_client_tool_fallback` reads (see
 * `shared/agent_base.py`) — a throw would surface the generic
 * "Sorry — I couldn't complete that" and tell the user nothing (AIVA-7724).
 */
async function emitTextPill(
  toolId: string,
  handler: string,
  label: string,
  text: unknown,
): Promise<{ rendered: boolean; message?: string }> {
  const t = stripInventedSectionHeading(_s(text));
  if (!t) throw new Error(`${toolId} requires non-empty text`);

  // Best-effort: a host that will not read the selection must not lose its
  // placement buttons over it, so an unreadable selection skips the check.
  let selection = "";
  try {
    selection = (await getWordSelection()) ?? "";
  } catch { /* selection unavailable on this host — fall through and emit */ }

  if (sameProse(t, selection)) {
    return {
      rendered: false,
      message:
        "That is the selected text unchanged, so there is nothing to place. " +
        "Tell me what to write and I'll draft something new, or use /improve or /polish to rework what is there.",
    };
  }

  // Second pass, and the one that catches a draft echoed from the anchored
  // window rather than from a selection. Runs only when the selection check did
  // not already answer, so an ordinary rewrite turn costs no extra search.
  if (await isEchoOfDocument(t)) {
    return {
      rendered: false,
      message:
        "That paragraph is already in the document, so there is nothing new to place. " +
        "Tell me what to add and I'll draft something that is not there yet, " +
        "or use /improve or /polish to rework the existing wording.",
    };
  }

  emitPills([{ type: "office_action", label: outlineAwareLabel(label, t), handler, args: { text: t } }]);
  return { rendered: true };
}

/** Emit one changes-list office_action pill (validates that the list is non-empty).
 *  Shared by `pill_word_apply_review` and `pill_word_apply_styles` — same shape,
 *  different array-element types. Sonar duplication fix.
 *
 *  The label carries the change count — the specification's "Variant A" bar
 *  reads ``Apply changes (4)``, so the user sees the blast radius before
 *  clicking. The count is exact: it is the length of the array the model
 *  supplied, which is the same list the handler will apply. */
function emitChangesPill<T>(
  toolId: string,
  handler: string,
  label: string,
  changes: unknown,
): { rendered: true } {
  const changeList = (Array.isArray(changes) ? changes : []) as T[];
  if (changeList.length === 0) {
    throw new Error(`${toolId} requires non-empty changes array`);
  }
  emitPills([
    {
      type: "office_action",
      label: `${label} (${changeList.length})`,
      handler,
      args: { changes: changeList },
    },
  ]);
  return { rendered: true };
}

/**
 * What to tell the user when a comment could not be placed.
 *
 * One message per cause. "Not found", "this Word is too old" and "the phrase is
 * there four times" used to arrive as the same bare `false`, which left the
 * model guessing and the user re-reading a document that was fine.
 */
function commentFailureMessage(phrase: string, result: CommentInsertResult): string {
  const quoted = `"${changeSnippet(phrase)}"`;
  switch (result.reason) {
    case "unsupported":
      return "AiVA can't add comments in this version of Word. Open the document in the Word desktop app.";
    case "ambiguous":
      // The recovery that keeps a review moving is a longer anchor, not a
      // question: contract boilerplate repeats, so "shall not be unreasonably
      // withheld" is ambiguous in almost every agreement, while the same phrase
      // with its clause number in front is unique.
      return (
        `${quoted} appears ${result.matches} times in the document, so AiVA didn't guess which one you meant. ` +
        `Quote a longer phrase that includes the clause number or nearby wording, ` +
        `or say which occurrence to comment on (1 to ${result.matches}).`
      );
    case "not_found":
      return `${quoted} isn't in the document, so nothing was commented on. The wording may have shifted since the review ran.`;
    default:
      return `The comment on ${quoted} could not be added.`;
  }
}

/**
 * The batch's own summary line, on the same terms as `reviewEditsMessage`:
 * silent on a clean run, explicit about what was left behind otherwise.
 */
function batchCommentMessage(batch: BatchCommentResult): string | undefined {
  if (batch.reason === "unsupported") {
    return "AiVA can't add comments in this version of Word. Open the document in the Word desktop app.";
  }
  const missed = batch.attempted - batch.inserted;
  if (missed === 0) return undefined;
  const ambiguous = batch.results.filter((r) => r.reason === "ambiguous").length;
  const detail = ambiguous > 0 ? ` ${ambiguous} matched more than one place in the document.` : "";
  if (batch.inserted === 0) {
    return `None of the ${batch.attempted} comment${batch.attempted === 1 ? "" : "s"} could be placed.${detail}`;
  }
  return `Added ${batch.inserted} of ${batch.attempted} comments; ${missed} could not be placed.${detail}`;
}

const SKIP_REASON_LABEL: Record<ReviewSkip["reason"], string> = {
  not_found: "not found",
  ambiguous: "appears more than once",
  too_long: "quote too long to search",
  failed: "Word refused the edit",
};

function countSkips(skips: ReviewSkip[], reason: ReviewSkip["reason"]): number {
  return skips.filter((s) => s.reason === reason).length;
}

/** How many skipped changes to name before summarising the rest. */
const MAX_NAMED_SKIPS = 3;

/**
 * The skipped changes, NAMED and quoted IN FULL.
 *
 * A count on its own ("1 could not be applied") leaves the user to work out
 * which of fourteen findings was dropped by diffing the card against the
 * document. The quote is the only handle they have on a finding, so it is what
 * the message carries (AIVA-7731).
 */
function skipList(skips: ReviewSkip[]): string {
  const named = skips
    .slice(0, MAX_NAMED_SKIPS)
    .map((s) => `"${s.original.replace(/\s+/g, " ").trim()}" (${SKIP_REASON_LABEL[s.reason]})`)
    .join(", ");
  return named + (skips.length > MAX_NAMED_SKIPS ? `, +${skips.length - MAX_NAMED_SKIPS} more` : "");
}


function skipAdvice(skips: ReviewSkip[]): string {
  const parts: string[] = [];
  if (countSkips(skips, "ambiguous") > 0) {
    parts.push("Quote a longer, unique phrase for anything that appears more than once.");
  }
  if (countSkips(skips, "too_long") > 0) {
    parts.push("Quote a shorter phrase for anything too long to search.");
  }
  if (parts.length === 0 && countSkips(skips, "not_found") > 0) {
    parts.push("The wording may have shifted since the review ran.");
  }
  return parts.length > 0 ? ` ${parts.join(" ")}` : "";
}

const MAX_LISTED_CHANGES = 10;

function listOverflow(total: number): string {
  return total > MAX_LISTED_CHANGES ? ` (+${total - MAX_LISTED_CHANGES} more)` : "";
}


function applyReviewMessage(
  attempted: number,
  applied: number,
  skipped: number,
  skips: ReviewSkip[],
): string | undefined {
  const named = skips.length > 0 ? ` Skipped: ${skipList(skips)}.` : "";
  const advice = skipAdvice(skips);
  if (attempted > 0 && applied === 0) {
    return (
      `None of the ${attempted} suggested change${attempted === 1 ? "" : "s"} could be applied — nothing was changed.` +
      named +
      advice
    );
  }
  if (skipped > 0) {
    return `Applied ${applied} of ${attempted} changes — ${skipped} could not be applied.` + named + advice;
  }
  return undefined;
}

/**
 * What to tell the user after an Apply review, or "" when there is nothing to
 * add.
 *
 * G-7: a finding anchored by exact matchText that no longer occurs cannot be
 * placed. Reporting only the successes is how a reviewer ends up believing a
 * 12-finding review is fully marked up when three were lost. Silence is
 * reserved for the clean apply, so the message means something when it appears.
 */
/** "One finding quotes" / "3 findings quote" — the subject of the notes below,
 *  built in one place so neither note carries a ternary inside a template. */
function findingsSubject(count: number, singularVerb: string, pluralVerb: string): string {
  if (count === 1) return `One finding ${singularVerb}`;
  return `${count} findings ${pluralVerb}`;
}

/** An anchor Word cannot search for never had a chance, and saying "the wording
 *  may have shifted" sent the user looking for a change to their document that
 *  had not happened (AIVA-7724). Named as its own cause, with the thing that
 *  actually helps. "" when there are none, so it appends to nothing. */
function tooLongNote(count: number): string {
  // `> 0` rather than `!== 0`: a result assembled elsewhere may not carry this
  // counter at all, and `undefined findings quote...` is worse than silence.
  if (count > 0) {
    const subject = findingsSubject(count, "quotes", "quote");
    return (
      ` ${subject} a passage too long for Word to search for — ` +
      "ask for the change against a shorter phrase and it will apply."
    );
  }
  return "";
}

/** A finding whose span sat inside another one's is already covered by the
 *  rewrite that replaced it — nothing was lost, so it must not read as a miss.
 *  Reporting it as "could not be located" is what made a complete review look
 *  partial (AIVA-7724). */
function overlapNote(count: number): string {
  if (count > 0) {
    const subject = findingsSubject(count, "was", "were");
    return (
      ` ${subject} already covered by a larger rewrite in the same sentence, ` +
      "so it needed no separate change."
    );
  }
  return "";
}

function reviewEditsMessage(result: ReviewEditResult): string {
  let counted = "";
  const tooLong = tooLongNote(result.tooLong);
  const overlap = overlapNote(result.overlapping);
  const findingWord = result.attempted === 1 ? "1 finding" : `${result.attempted} findings`;
  if (result.attempted > 0 && result.applied === 0) {
    // Only when nothing at all landed does the vague fallback earn its place —
    // and never over a cause we actually know.
    const cause = tooLong || " The wording may have shifted since the review ran.";
    counted = `None of the ${findingWord} could be located in the current text — nothing was changed.${cause}`;
  } else if (result.skipped > 0) {
    counted =
      `Applied ${result.applied} of ${result.attempted} findings; ` +
      `${result.skipped} could not be located in the current text.${tooLong}${overlap}`;
  } else if (result.overlapping > 0) {
    // Everything anchorable landed; the only shortfall is the covered ones, so
    // the line has to say the review IS complete or the count reads as a loss.
    counted = `Applied ${result.applied} of ${result.attempted} findings.${overlap}`;
  }
  // A host that could not force Track Changes wrote the suggestions as plain
  // edits. Say so — an audit trail the user believes in but does not have is
  // worse than none.
  const untracked =
    result.applied > 0 && !result.tracked
      ? " Track Changes could not be turned on in this version of Word, so the wording changes are not marked as revisions."
      : "";
  return `${counted}${untracked}`.trim();
}

/**
 * The repeating shape of a heading list, computed rather than inferred.
 *
 * A long agreement is usually the same block of sections repeated per schedule
 * or per statement of work, so the useful answer is "20 sections, 84 times, and
 * here is the one that does not fit" — not 1,669 lines. Deriving that from the
 * raw list is arithmetic (strip the number, group, count), and arithmetic is
 * exactly what a model should not be doing by eye.
 *
 * `distinct` keeps first-seen order, so the caller can print it as the outline.
 * `anomalies` is the payload: a section appearing once in a repeating document
 * is either the real content or a defect, and it is the first thing a reviewer
 * needs to see.
 */
function summariseHeadingShape(headings: ReadonlyArray<{ level: number; text: string }>): {
  total: number;
  distinct: Array<{ name: string; count: number; level: number }>;
  repeats: number;
  anomalies: string[];
} {
  // Leading enumerator only: "12.", "3.4", "1334A." — never a number that is part
  // of the section's actual name ("Schedule 2" must stay "Schedule 2").
  const stripNumber = (text: string) => text.replace(/^\s*\d+[A-Za-z]?(?:\.\d+)*[.)]?\s+/, "").trim();

  const order: string[] = [];
  const byName = new Map<string, { count: number; level: number }>();
  for (const h of headings) {
    const name = stripNumber(h.text) || h.text.trim();
    const seen = byName.get(name);
    if (seen) {
      seen.count += 1;
    } else {
      byName.set(name, { count: 1, level: h.level });
      order.push(name);
    }
  }

  const distinct = order.map((name) => ({ name, ...byName.get(name)! }));
  // The document's Title appears once by definition, so it would otherwise be
  // reported as a section that breaks the pattern. It is not a section at all.
  const isTitle = (level: number) => level <= 0;
  // How many times the main block repeats — the modal count, not the maximum, so
  // one section appearing in every schedule plus once more does not inflate it.
  const counts = distinct.map((d) => d.count).filter((n) => n > 1);
  const repeats = counts.length > 0 ? Math.max(...counts) : 1;
  // Anything appearing once in a document that plainly repeats. Reported with the
  // ORIGINAL text, numbering included, because the number is how the user finds it.
  const oneOff = new Set(distinct.filter((d) => d.count === 1 && !isTitle(d.level)).map((d) => d.name));
  const anomalies = repeats > 1 ? headings.filter((h) => oneOff.has(stripNumber(h.text) || h.text.trim())).map((h) => h.text) : [];

  return { total: headings.length, distinct, repeats, anomalies };
}

/**
 * A section's outline level, whichever shape it arrives in.
 *
 * "title" | "1" | "2" | "3" from the tool schema, or 1 | 2 | 3 as numbers when the
 * model reads them off `toc`. Both are legitimate; only the string form used to
 * survive. Out-of-range levels fall back to 1 rather than throwing — a heading at
 * the wrong level is recoverable, a failed insert is not.
 */
function normaliseOutlineLevel(raw: unknown): "title" | 1 | 2 | 3 {
  const text = (typeof raw === "number" ? String(raw) : _s(raw)).trim().toLowerCase();
  if (text === "title") return "title";
  const n = Number(text);
  return n === 2 || n === 3 ? (n as 2 | 3) : 1;
}

/**
 * This turn's `word_get_document_structure` sweep, handed to the insert pill so
 * READ MODE does not sweep the whole document a second time.
 *
 * AIVA-8025. `pill_word_insert_outline` was budgeted 5000ms when BUILD MODE was
 * its only caller and it merely reformatted sections the model had already
 * written — no Office.js round trip at all. READ MODE's `depth` path
 * (AIVA-7893) then had it re-read every paragraph, which is the SAME sweep
 * `word_get_document_structure` is given 15000ms for, and the 5000 came along
 * unchanged. On a long document that sweep loses runHandler's Promise.race, the
 * handler never reaches `emitPills`, and the turn renders the outline card (a
 * 15s tool) with no Insert/Copy row beneath it — the ticket's missing pill.
 *
 * Reusing the sweep removes the second read rather than widening its budget.
 * The budget is widened too, because the fallback below still takes one.
 *
 * Single use, and stored only from a read that actually succeeded:
 *   - the first pill to read it consumes it, so the Insert click that follows —
 *     which WRITES headings into the document — can never be served a map taken
 *     before its own write;
 *   - a follow-up 'now show level 2' lands in a LATER turn with the entry spent
 *     and re-reads, which is what picks up any edit made in between;
 *   - the TTL bounds the one case consumption does not: a turn that reads the
 *     structure and then never calls the pill.
 *
 * A refused or empty read is deliberately NOT stored. Caching one would turn the
 * pill's own read from a second chance into a guaranteed throw.
 */
const OUTLINE_TOC_CACHE_MS = 30_000;
let cachedOutlineToc: { toc: readonly TocEntry[]; at: number } | null = null;

/**
 * When the structure read last saw a document with an outline of its OWN.
 *
 * Separate from `cachedOutlineToc`, and on a much longer window, because it
 * answers a different question. A cached paragraph MAP goes stale the moment
 * anything is written, so 30s is right for it. "Does this document have
 * headings" does not go stale that way — and a 30s bound on it was the bug:
 * retyping 171 headings into `sections` takes the model minutes, so the window
 * had closed by the time the truncated payload arrived and the retype was used.
 */
const OUTLINE_READ_MODE_MS = 600_000;
let outlineReadModeAt: number | null = null;

function cacheOutlineToc(toc: readonly TocEntry[], readOk: boolean): void {
  const stored = readOk && toc.length > 0;
  cachedOutlineToc = stored ? { toc, at: Date.now() } : null;
  outlineReadModeAt = stored ? Date.now() : null;
}

/** True while the structure read says the document has its own outline. */
function documentHasOwnOutline(): boolean {
  return outlineReadModeAt !== null && Date.now() - outlineReadModeAt <= OUTLINE_READ_MODE_MS;
}

/**
 * Whether a `sections` payload is the document's OWN headings typed back.
 *
 * This is what separates the bug from legitimate authorship, and both arrive
 * identically — as `sections` on a document that has headings. A retype's
 * headings ARE the document's, usually a truncated prefix of them; a skeleton
 * generated by /skeleton or /template invents names the document does not carry.
 * So compare the text: every supplied heading already in the document means
 * nothing was authored, and the document's own list is the better copy of it.
 *
 * Requires at least one supplied heading to match, so an empty or unrelated
 * payload is never read as a retype.
 */
function sectionsAreDocumentHeadings(
  supplied: readonly string[],
  rows: readonly OutlineRow[],
): boolean {
  if (supplied.length === 0 || rows.length === 0) return false;
  const own = new Set(rows.map((r) => r.text.trim().toLowerCase()));
  return supplied.every((h) => own.has(h.trim().toLowerCase()));
}

/** The sweep this turn already took, or null. Consumes it — see `cacheOutlineToc`. */
function takeCachedOutlineToc(): readonly TocEntry[] | null {
  const hit = cachedOutlineToc;
  cachedOutlineToc = null;
  if (!hit) return null;
  return Date.now() - hit.at <= OUTLINE_TOC_CACHE_MS ? hit.toc : null;
}

/**
 * Test seam. The cache is module state and outlives a single `it`, so a spec
 * that runs `word.get_document_structure` would otherwise leave a toc behind for
 * whichever outline-pill spec happens to run next — which is a stale read in the
 * one place these tests exist to prove there isn't one.
 */
export function __resetOutlineTocCache(): void {
  cachedOutlineToc = null;
  outlineReadModeAt = null;
}

/**
 * The document's own outline as ROWS AND SECTIONS, for READ MODE's insert pill.
 *
 * `depth` counts in normalised levels, so "1" is the document's own top level
 * whatever Word happens to call it — the same scale `outlineFromToc` renders.
 * Anything non-numeric means every level.
 *
 * `stub` is empty on purpose: a read outline has no guidance to add, and
 * inventing one would write text into the document that was never in it.
 */
async function outlineFromDocument(depth: unknown): Promise<{
  rows: readonly OutlineRow[];
  sections: Array<Record<string, unknown>>;
}> {
  const parsed = typeof depth === "number" ? depth : Number.parseInt(_s(depth), 10);
  const maxDepth = Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  // The depth filter is applied HERE, not at cache time: the cached entry is the
  // document's full toc, so a follow-up depth change re-filters the same headings
  // instead of narrowing something already narrowed.
  const toc = takeCachedOutlineToc() ?? (await readWordDocumentMap()).toc;
  const rows = outlineFromToc(toc, maxDepth).filter((row) => row.text.trim());
  return {
    rows,
    // outlineRowsToSections, not a local map: the render layer builds the same
    // pill from the same function, so the button the model triggers and the button
    // the add-in synthesises carry identical sections — including the Title/1/2/3
    // clamp the insert path needs (normaliseOutlineLevel accepts its numeric levels).
    sections: outlineRowsToSections(rows) as unknown as Array<Record<string, unknown>>,
  };
}

function resolveTableInsertPosition(raw: unknown): "append_to_end" | "at_cursor" | "replace_selection" {
  if (raw === "append_to_end") return "append_to_end";
  if (raw === "replace_selection") return "replace_selection";
  return "at_cursor";
}

// Some Office hosts load the add-in iframe without `clipboard-write` in their
// Permissions-Policy, which makes `navigator.clipboard.writeText` throw. The
// pill click is a real user gesture, so the legacy `document.execCommand('copy')`
// path still works — fall back to it whenever the async API is missing or rejects.
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// The pill `text` payload keeps '# ' heading and '- ' bullet markers (plain
// text everywhere else). Convert those markers to semantic HTML so a clipboard
// paste into Word produces real Heading 1-3 styles and bullet lists instead of
// literal '#' characters (AIVA-5927 /template paste formatting).
//
// FALLBACK ONLY since AIVA-7074: `word.copy_text` renders through
// buildWordClipboardHtml, which carries the document's font and handles tables,
// numbered lists and inline markers that this function does not. This one runs
// when the text parses to no blocks at all, so the copy never comes back empty.
//
// A pasted heading arrives in Word as BOLD TEXT, not as a paragraph bound to
// Word's "Heading 1" style — so the Navigation Pane in the destination document
// stays empty. Two fixes were tried and neither worked in the host: an
// mso-style-name document wrapper, and intercepting the copy event to bypass the
// browser's DOM serialiser. Word appears to honour those hints only for
// clipboard content it accepts as Word-originated, which a browser-written
// text/html flavour may never qualify as.
//
// Not pursued further because it is out of scope in both directions: neither
// TO-01 nor TO-02 has an acceptance criterion about the clipboard (both are
// about insertion into the attached document, which DOES produce real styles via
// the Word API), and this fallback is shared by every copy pill in the add-in —
// so an unverifiable rewrite here risks paths that currently work. Revisit as
// its own ticket, with a real host to test against.
export function markdownToClipboardHtml(text: string): string {
  const out: string[] = [];
  let inList = false;
  const closeList = () => {
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) {
      closeList();
      continue;
    }
    const heading = /^(#{1,3}) +(\S.*)$/.exec(line);
    if (heading) {
      closeList();
      const level = heading[1]!.length;
      out.push(`<h${level}>${escapeHtml(heading[2]!)}</h${level}>`);
      continue;
    }
    if (/^-\s+/.test(line)) {
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      out.push(`<li>${escapeHtml(line.replace(/^-\s+/, ""))}</li>`);
      continue;
    }
    closeList();
    out.push(`<p>${escapeHtml(line)}</p>`);
  }
  closeList();
  return out.join("");
}

// Rich-flavour twin of execCommandCopyFallback: selects a hidden contentEditable
// rendering of the HTML so execCommand('copy') captures text/html alongside the
// plain flavour — what Word needs to paste styled headings and bullets.
function execCommandCopyRichFallback(html: string): boolean {
  if (typeof document === "undefined") return false;
  const container = document.createElement("div");
  container.setAttribute("contenteditable", "true");
  container.style.position = "fixed";
  container.style.left = "-9999px";
  container.innerHTML = html;
  document.body.appendChild(container);
  try {
    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    // execCommand is deprecated, but it's the only clipboard write available
    // in iframes whose Permissions-Policy omits `clipboard-write`. NOSONAR
    const ok = document.execCommand("copy"); // NOSONAR: typescript:S1874
    selection.removeAllRanges();
    return ok;
  } catch {
    return false;
  } finally {
    container.remove();
  }
}

/** Apply RV-04 for one verb. Shared so accept and reject cannot drift apart in
 *  their count-checking or their messages — only the word changes. */
async function applyTrackedChanges(mode: "accept" | "reject", expectedCount: unknown) {
  const expected = typeof expectedCount === "number" ? expectedCount : undefined;
  const result = await applyWordTrackedChanges(mode, expected);
  const verb = mode === "accept" ? "Accepted" : "Rejected";

  if (result.applied) {
    recordWordChange(`${verb} tracked changes`, `${result.count} change(s)`);
    emitToast({ title: `${verb} ${result.count} tracked change(s)`, intent: "success" });
    return { applied: true, mode, count: result.count, message: `${verb} ${result.count} tracked change(s).` };
  }
  const messages: Record<string, string> = {
    none: "No tracked changes in this document — nothing was changed.",
    count_moved:
      `The document changed since you approved this — it now has ${result.actual} tracked changes, ` +
      `not ${result.expected}. Nothing was changed; ask again to see the current count.`,
    unsupported: "AiVA can't reach tracked changes in this version of Word.",
    failed: "The tracked changes could not be updated — the document may be protected. Nothing was changed.",
  };
  return { applied: false, mode, reason: result.reason, message: messages[result.reason ?? "failed"] };
}

/**
 * The re-count guard for a document-wide swap (G-11, first half).
 *
 * The approval card counted the matches, then the user approved THAT number. If
 * the document has moved underneath the card — an edit, or an undo, between the
 * count and the click — replacing a different number of occurrences is not the
 * operation that was authorised, so refuse rather than write.
 *
 * Returns the abort payload, or null when the swap is still the approved one:
 * no `expected_count` was sent, the count still agrees, or the document could
 * not be read at all (in which case the write proceeds as it did before the
 * guard existed).
 */
async function replaceAllAbortIfCountMoved(
  find: string,
  matchCase: boolean,
  expectedCount: unknown,
): Promise<{ count: number; aborted: true; message: string } | null> {
  if (typeof expectedCount !== "number") return null;
  const recount = await getWordOccurrences(find, 0, matchCase);
  if (!recount || recount.count === expectedCount) return null;
  return {
    count: 0,
    aborted: true,
    message:
      `The document changed since I counted: “${changeSnippet(find)}” now matches ${recount.count} ` +
      `time${recount.count === 1 ? "" : "s"}, not ${expectedCount}. Nothing was replaced — ask again to re-count.`,
  };
}

/**
 * Window size for an ANCHORED context read, in paragraphs either side. Ten
 * paragraphs make roughly a page, so 30 is about three pages — the size DE-01's
 * drafting flow asks for. Only a fallback: an explicit count always wins.
 */
const ANCHORED_WINDOW_PARAGRAPHS = 30;

// AIVA-7516: runHandler's timeout enforcement (integrations/tools/index.ts)
// is a Promise.race against a setTimeout — the losing side was never
// cancelled (AIVA-8199 now signals it; this guard stays as the second line), so a word.docreview.scan call that loses that race keeps
// running in the background even after the chat turn has already reported
// failure and (usually) been retried. Without this guard, that orphaned
// scan would still reach bulkCreateFindings() later and persist a SECOND,
// differently-id'd copy of every finding the retry's own scan is about to
// persist — a real duplicate-data bug, not just wasted work. Each new scan
// call supersedes the last, so only the most recent one is ever allowed to
// persist; every earlier, now-abandoned scan's late completion becomes a
// silent no-op instead.
let latestDocReviewScanAttempt = 0;

/** AIVA-8199: a scan the chat gave up on (timeout or Stop) must not save, or
 *  its cards contradict the reply. Thrown between steps so it also stops early. */
const EMAIL_IMAGE_MARKER_RE = /\[\[IMG:([A-Za-z0-9._%+@-]+)\]\]/g;

/** Base64 bytes for the body's embedded images, keyed by content id. Returns `{}`
 *  on any failure so the paste still goes ahead with "[Image]" placeholders. */
async function resolveEmailImages(
  body: string,
  messageId: string,
  mailbox: string | null,
  ctx: HandlerContext | undefined,
): Promise<Record<string, string>> {
  const contentIds = [...new Set([...body.matchAll(EMAIL_IMAGE_MARKER_RE)].map((m) => m[1]!))];
  if (contentIds.length === 0 || !messageId || !ctx?.apiToken) return {};
  try {
    const images = await fetchEmailInlineImages(messageId, contentIds, mailbox, ctx.apiToken, ctx.refresh);
    return Object.fromEntries(Object.entries(images).map(([cid, image]) => [cid, image.data]));
  } catch (error) {
    console.warn("[word] embedded email images unavailable — inserting placeholders", error);
    return {};
  }
}

function throwIfScanCancelled(ctx: HandlerContext | undefined): void {
  if (ctx?.signal?.aborted) throw new Error("word.docreview.scan was cancelled");
}

// AIVA-7506 perf follow-up: word_docreview_scan may receive an OPTIONAL
// `rules` hint — the SAME rule-key vocabulary as word_docreview_judge's own
// argument (deterministic keys missing_hyperlink, broken_reference,
// bracket_heading_mismatch, inconsistent_formatting, exhibit_schedule_mismatch,
// defined_term_section_mismatch, shortened_hyperlink_text, defined_not_used,
// alphabetical_order, numbering_order, missing_defined_term,
// ambiguous_reference, used_before_defined; judgment keys topic_mismatch,
// exhibit_schedule_ambiguous, hyperlink_target_mismatch, used_not_defined) —
// naming which rule(s) the current ask cares about. This is a
// PERFORMANCE-ONLY signal: skipping a sweep below only
// saves an Office.js round trip, it never affects correctness. Real
// scoping enforcement lives entirely in word_docreview_judge's own pruning
// step (docreview_tools.py's _judge/_prune_out_of_scope_findings) — a
// missing or wrong hint here just means the full sweep runs, exactly as it
// always has; it can never cause the wrong findings to be shown.
function parseDocReviewRulesHint(args: Record<string, unknown> | undefined): Set<string> | null {
  const rules = args?.rules;
  if (!Array.isArray(rules)) return null;
  const upper = rules.filter((r): r is string => typeof r === "string").map((r) => r.toUpperCase());
  return upper.length > 0 ? new Set(upper) : null;
}

type RulesHintPredicate = (...keys: string[]) => boolean;

/** `wants(...)` for one scan call: true for every rule when the hint is
 * absent (the full-sweep default, unchanged) or when any given key is
 * actually requested. See `parseDocReviewRulesHint` for why the hint is
 * only ever a performance signal. */
function buildRulesHintPredicate(args: Record<string, unknown> | undefined): RulesHintPredicate {
  const hint = parseDocReviewRulesHint(args);
  return (...keys: string[]): boolean => !hint || keys.some((k) => hint.has(k));
}

/**
 * The sweeps that depend on nothing else, run as ONE `Promise.all`.
 *
 * AIVA-7516: these used to run one after another — 13 sequential `Word.run()`
 * round trips, which timed out on a real 40-page document (confirmed via a
 * Langfuse trace: both attempts failed at exactly 20000ms, runHandler's
 * client-side timeout, not a server error). Batching respects the only real
 * dependencies, which is why `runDependentSweeps` is a separate second wave.
 *
 * broken_reference's sweep feeds missing_hyperlink/topic_mismatch/
 * inconsistent_formatting (references) and numbering_order/ambiguous_reference
 * (headings) directly, plus hyperlink_target_mismatch's judgment step needs
 * its headings as read-only context — so it still runs whenever
 * hyperlink_target_mismatch is requested, even though it has no deterministic
 * finding of its own.
 *
 * hyperlink_target_mismatch used to sit in THIS wave, back when it only
 * enumerated HYPERLINK fields. AIVA-8310 gave it a second pass over the
 * `w:hyperlink` element form, which searches the reference texts
 * broken_reference found — so it now has a real dependency and has moved to
 * the second wave with the others.
 */
/**
 * Run one docreview sweep and log how long it took (AIVA-8315).
 *
 * The scan has a 60s client-side limit, and a slow run surfaced only as "the
 * document review scan timed out" with no way to tell which sweep was slow —
 * the sweeps run in parallel, so a total alone says nothing. One line per
 * sweep at `info`, in the same `[docReview]` namespace as every other log here.
 */
function timedSweep<T>(rule: string, run: () => Promise<T>): Promise<T> {
  if (scanProfilingEnabled()) return profiledSweep(rule, run);
  const started = performance.now();
  return run().finally(() => {
    console.info(`[docReview] scan: ${rule} sweep took ${Math.round(performance.now() - started)}ms`);
  });
}

/** AIVA-8925: the same sweep, run alone and with its Word calls counted — see
 *  `scanProfiler.ts`. Timed from when it starts running, not when it queued. */
async function profiledSweep<T>(rule: string, run: () => Promise<T>): Promise<T> {
  let started = 0;
  const { result, counts } = await profileSweep(() => {
    started = performance.now();
    return run();
  });
  console.info(
    `[docReview] profile: ${rule} took ${Math.round(performance.now() - started)}ms alone, ` +
      `${counts.syncs} Word round trips, ${counts.compares} location compares`,
  );
  return result;
}

async function runIndependentSweeps(wants: RulesHintPredicate): Promise<{
  brokenReference: BrokenReferenceSweep;
  bracketHeadingMismatch: BracketHeadingMismatchSweep;
  exhibitSchedule: ExhibitScheduleMismatchSweep;
  definedTermSectionMismatch: DefinedTermSectionMismatchSweep;
  shortenedHyperlinkText: ShortenedHyperlinkTextSweep;
  definedNotUsed: DefinedNotUsedSweep;
  alphabeticalOrder: AlphabeticalOrderSweep;
  missingDefinedTerm: MissingDefinedTermSweep;
  usedBeforeDefined: UsedBeforeDefinedSweep;
}> {
  const needBrokenReference = wants(
    "BROKEN_REFERENCE",
    "MISSING_HYPERLINK",
    "TOPIC_MISMATCH",
    "INCONSISTENT_FORMATTING",
    "NUMBERING_ORDER",
    "AMBIGUOUS_REFERENCE",
    "HYPERLINK_TARGET_MISMATCH",
  );
  const [brokenReference, bracketHeadingMismatch, exhibitSchedule, definedTermSectionMismatch, shortenedHyperlinkText, definedNotUsed, alphabeticalOrder, missingDefinedTerm, usedBeforeDefined] = await Promise.all([
    needBrokenReference
      ? timedSweep("broken_reference", () => findBrokenReference())
      : Promise.resolve<BrokenReferenceSweep>({ references: [], headings: [] }),
    wants("BRACKET_HEADING_MISMATCH")
      ? timedSweep("bracket_heading_mismatch", () => findBracketHeadingMismatch())
      : Promise.resolve<BracketHeadingMismatchSweep>({ references: [], headings: [] }),
    wants("EXHIBIT_SCHEDULE_MISMATCH", "EXHIBIT_SCHEDULE_AMBIGUOUS")
      ? timedSweep("exhibit_schedule_mismatch", () => findExhibitScheduleMismatch())
      : Promise.resolve<ExhibitScheduleMismatchSweep>({ mentions: [] }),
    wants("DEFINED_TERM_SECTION_MISMATCH")
      ? timedSweep("defined_term_section_mismatch", () => findDefinedTermSectionMismatch())
      : Promise.resolve<DefinedTermSectionMismatchSweep>({ candidates: [] }),
    wants("SHORTENED_HYPERLINK_TEXT")
      ? timedSweep("shortened_hyperlink_text", () => findShortenedHyperlinkText())
      : Promise.resolve<ShortenedHyperlinkTextSweep>({ fields: [] }),
    wants("DEFINED_NOT_USED")
      ? timedSweep("defined_not_used", () => findDefinedNotUsed())
      : Promise.resolve<DefinedNotUsedSweep>({ terms: [] }),
    wants("ALPHABETICAL_ORDER")
      ? timedSweep("alphabetical_order", () => findAlphabeticalOrder())
      : Promise.resolve<AlphabeticalOrderSweep>({ definitions: [] }),
    wants("MISSING_DEFINED_TERM")
      ? timedSweep("missing_defined_term", () => findMissingDefinedTerm())
      : Promise.resolve<MissingDefinedTermSweep>({ terms: [], definitionsSectionText: "" }),
    wants("USED_BEFORE_DEFINED")
      ? timedSweep("used_before_defined", () => findUsedBeforeDefined())
      : Promise.resolve<UsedBeforeDefinedSweep>({ terms: [] }),
  ]);
  return { brokenReference, bracketHeadingMismatch, exhibitSchedule, definedTermSectionMismatch, shortenedHyperlinkText, definedNotUsed, alphabeticalOrder, missingDefinedTerm, usedBeforeDefined };
}

/**
 * The second wave — every sweep that needs the first wave's results.
 *
 * Reuses brokenReference's already-swept references (see findInconsistentFormatting's
 * docstring). Without that, inconsistent_formatting silently re-ran the entire
 * broken_reference paragraph sweep a second time, on top of the round trip
 * broken_reference already cost. hyperlink_target_mismatch reuses them for the
 * same reason (AIVA-8310).
 */
async function runDependentSweeps(
  wants: RulesHintPredicate,
  brokenReference: BrokenReferenceSweep,
): Promise<{
  inconsistentFormatting: InconsistentFormattingSweep;
  topicMismatch: TopicMismatchSweep;
  missingHyperlink: MissingHyperlinkSweep;
  usedNotDefined: UsedNotDefinedSweep;
  hyperlinkTarget: HyperlinkTargetMismatchSweep;
}> {
  const [inconsistentFormatting, topicMismatch, missingHyperlink, usedNotDefined, hyperlinkTarget] = await Promise.all([
    wants("INCONSISTENT_FORMATTING")
      ? timedSweep("inconsistent_formatting", () => findInconsistentFormatting(brokenReference.references))
      : Promise.resolve<InconsistentFormattingSweep>({ references: [] }),
    wants("TOPIC_MISMATCH")
      ? timedSweep("topic_mismatch", () => findTopicMismatchData(brokenReference.references))
      : Promise.resolve<TopicMismatchSweep>({ candidates: [] }),
    wants("MISSING_HYPERLINK")
      ? timedSweep("missing_hyperlink", () => findMissingHyperlink(brokenReference.references, brokenReference.headings))
      : Promise.resolve<MissingHyperlinkSweep>({ references: [], headings: [] }),
    wants("USED_NOT_DEFINED")
      ? timedSweep("used_not_defined", () => findUsedNotDefinedCandidates())
      : Promise.resolve<UsedNotDefinedSweep>({ candidates: [] }),
    wants("HYPERLINK_TARGET_MISMATCH")
      ? timedSweep("hyperlink_target_mismatch", () => findHyperlinkTargetMismatchData(brokenReference.headings))
      : Promise.resolve<HyperlinkTargetMismatchSweep>({ targets: [] }),
  ]);
  return { inconsistentFormatting, topicMismatch, missingHyperlink, usedNotDefined, hyperlinkTarget };
}

/**
 * Report the document's live comments so the server can reopen comment fixes
 * that are no longer there (AIVA-8280).
 *
 * Why this exists at all: a fix that REWRITES the document is self-checking —
 * if this scan re-found the location, the rewrite is gone, so the server needs
 * nothing from us. A COMMENT fix changes nothing the rule inspects, so the
 * rule stays true forever and re-detection says nothing about whether the
 * comment survived a Ctrl+Z. Only this side can look.
 *
 * Fails CLOSED in both directions that matter:
 *
 *  - `getWordComments()` returns null when the host has no comments API
 *    (pre-WordApi 1.4) or the read threw. Posting an empty list then would be
 *    the statement "this document has no comments", which would reopen every
 *    comment fix on it. Post nothing instead and leave today's behaviour.
 *  - Any failure of the call itself is swallowed. Reconciliation is an
 *    improvement to the cycle, not a precondition for it — a review that
 *    cannot reconcile is still worth running, and throwing here would take
 *    down a scan that has already done all its work.
 */
/**
 * Drop findings the document already carries AiVA's own comment for (AIVA-8538).
 *
 * The server's own re-flag guard reads prior findings by THREAD, so clearing the
 * chat mints a new thread, finds no prior decisions, and re-offers a location the
 * operator already resolved — while AiVA's comment for it is still sitting in the
 * document. The document outlives the thread, so the document is what a fresh
 * cycle has to ask. Same principle `reconcile_findings` states server-side; this
 * applies it across threads, where a thread-scoped read cannot reach.
 *
 * Matched on (anchor text + comment text) TOGETHER, never on comment text alone:
 * every broken_reference writes the identical "section reference not found", so
 * text alone would silence every broken-reference finding in the document the
 * moment one comment existed anywhere in it — hiding real defects, which is far
 * worse than the duplicate card this removes.
 *
 * Deliberately SUBTRACTIVE and nothing else. It filters what this cycle creates;
 * it never edits, re-statuses or deletes a stored finding, and never touches the
 * document or its comments. If it cannot read comments, it suppresses nothing and
 * the cycle behaves exactly as it does today — the failure direction is always
 * "show the card", never "hide it".
 */
async function withoutAlreadyCommentedFindings(findings: FindingCreate[]): Promise<FindingCreate[]> {
  if (findings.length === 0) return findings;
  let comments: AnchoredWordComment[] | null = null;
  try {
    comments = await getAnchoredWordComments();
  } catch (err) {
    console.warn("[docReview] comment-based suppression skipped:", err);
    return findings;
  }
  // null = could not look. Suppressing on an unread document would hide real
  // findings, so an unreadable read suppresses nothing.
  if (comments === null || comments.length === 0) return findings;

  const commented = new Set(
    comments
      .filter((c) => c.anchorText && c.text)
      .map((c) => `${c.anchorText.toLowerCase()}\u0000${c.text.toLowerCase()}`),
  );
  if (commented.size === 0) return findings;

  return findings.filter((f) => {
    // A rule whose fix REWRITES the document is out of scope: re-finding its
    // location proves the rewrite is gone, so the server's own guard already
    // handles it and suppressing here would defeat that. Only a comment-only
    // fix leaves the rule's condition true forever, which is the case this
    // exists for. `comment` is the text the fix writes.
    const commentText = typeof f.comment === "string" ? f.comment.trim() : "";
    const matchText = typeof f.fix_params?.matchText === "string" ? f.fix_params.matchText.trim() : "";
    if (!commentText || !matchText) return true;
    return !commented.has(`${matchText.toLowerCase()}\u0000${commentText.toLowerCase()}`);
  });
}

async function reconcileCommentFixes(threadId: string, ctx?: HandlerContext): Promise<void> {
  try {
    const comments = await getWordComments();
    if (comments === null) return;
    await reconcileFindings(
      threadId,
      comments.map((c) => c.text),
      comments.map((c) => c.id).filter((id) => id !== ""),
      ctx?.apiToken ?? null,
      ctx?.refresh,
    );
  } catch (err) {
    console.warn("[docReview] comment reconciliation skipped:", err);
  }
}

/**
 * The refusal `word.docreview.scan` must return when the document cannot be
 * written to, or `null` when it can and the scan should proceed.
 *
 * A read-only document (Word's "Viewing" mode, a file opened read-only,
 * Protected View) cannot be written to, and the scan's very next statement —
 * `cleanupAllAnchors()` — is a WRITE. Left unguarded it throws, the scan dies
 * before a single rule is swept, and the turn reports "the document scan
 * failed - please try again": advice that cannot work, because every retry in
 * Viewing mode fails the same way.
 *
 * This is the only Word skill that writes during the turn rather than
 * deferring it to a pill the user clicks (see `emitPills`), which is why
 * /improve and friends work fine in Viewing mode and only the check family
 * breaks. Refuse with something the user can act on instead.
 */
async function docReviewReadOnlyRefusal(): Promise<{ readOnly: true; message: string } | null> {
  if (!(await isDocumentReadOnly())) return null;
  return {
    readOnly: true,
    message:
      "This document is open in Viewing mode, so I can't run the check. " +
      "The review places hidden markers in the document so you can jump to " +
      "and apply each finding, which needs edit access. Switch to Editing " +
      "(Review tab → Editing) and ask me again.",
  };
}

const SORT_CHOICE_OPTIONS: ReadonlyArray<{ label: string; phrase: string }> = [
  { label: "Sort by abbreviation", phrase: "sorting acronyms by abbreviation" },
  { label: "Sort by full form", phrase: "sorting acronyms by full form" },
];

/** What the re-run asks for, in words the skill's prompt maps to the same
 *  scope: the click must check exactly what the operator first asked. */
function sortChoiceRequest(hint: Set<string> | null): { skillKey: string; ask: string } {
  if (hint?.size === 1 && hint.has("ALPHABETICAL_ORDER")) {
    return { skillKey: "word_docreview_definedterms", ask: "Check my definitions are in alphabetical order" };
  }
  // Every other list that names alphabetical order re-runs the defined-terms
  // check: it is the only live skill that runs that rule. The full
  // /check-references skill (word_docreview) these pills used to re-run was
  // retired by AIVA-8538, so a click there went nowhere.
  return { skillKey: "word_docreview_definedterms", ask: "Check my defined terms" };
}

/**
 * AIVA-8606: the operator chooses how acronyms are ordered, every run, before
 * the alphabetical check runs — a list files "GAAP" either under its letters or
 * under "generally accepted…", and nothing in the document says which.
 *
 * Asked BEFORE the scan touches the document: every scan starts by clearing all
 * review anchors, so running the other rules first and asking afterwards would
 * wipe their findings when the choice re-runs the scan. The two pills re-run
 * the same check with the choice spelled out, pinned to the skill that ran it
 * (the router would otherwise re-classify the click); the backend refuses a
 * forced skill the persona lacks, so a wrong guess fails safe. The visible
 * message is the operator's confirmation of what they chose.
 */
function docReviewSortChoice(
  args: Record<string, unknown> | undefined,
): { sortChoiceNeeded: true; message: string } | null {
  const sortKey = args?.sortKey;
  if (sortKey === "abbreviation" || sortKey === "full_form") return null;
  // Asked only when the alphabetical check was EXPLICITLY requested. A scan
  // with no rules list is read as "every rule" by buildRulesHintPredicate, and
  // used to ask here too — so a typed cross-reference request ("check for the
  // issues in cross reference"), whose model often omits the optional list,
  // stopped to ask about acronyms, checked nothing, and the cross-references
  // skill reported "No cross-reference problems were found" every time
  // (AIVA-8875 testing). /check-defined-terms always passes its full list, so
  // it still asks. With no list, alphabetical order runs by abbreviation.
  if (!parseDocReviewRulesHint(args)?.has("ALPHABETICAL_ORDER")) return null;

  const { skillKey, ask } = sortChoiceRequest(parseDocReviewRulesHint(args));
  emitPills(
    SORT_CHOICE_OPTIONS.map(({ label, phrase }) => ({
      type: "chat_intent" as const,
      label,
      message: `${ask}, ${phrase}.`,
      forcedSkillKey: skillKey,
    })),
  );
  return {
    sortChoiceNeeded: true,
    message:
      "Before I check the alphabetical order, choose how acronyms are sorted: by their letters " +
      '("GAAP" under G-A) or by the words they stand for ("generally accepted accounting ' +
      'principles" under G-E). Pick one of the options below.',
  };
}

/** How the alphabetical check ordered acronyms, for the reply: an acronym
 *  sorted by full form whose entry never spells it out was sorted by its
 *  letters instead, and the operator is told which. */
function sortOrderSummary(
  args: Record<string, unknown> | undefined,
  sweep: AlphabeticalOrderSweep,
): { sortKey: DefinitionSortKey; acronymsSortedByAbbreviation: string[] } {
  const sortKey = parseDefinitionSortKey(args?.sortKey);
  return {
    sortKey,
    acronymsSortedByAbbreviation: sortKey === "full_form" ? acronymsWithoutFullForm(sweep.definitions) : [],
  };
}

/** Runs a synchronous handler body so a throw rejects the returned Promise (as an async function would). */
function asPromise(fn: (args: Record<string, unknown>) => unknown): HandlerFn {
  return (args) => {
    try {
      return Promise.resolve(fn(args));
    } catch (err) {
      return Promise.reject(err);
    }
  };
}

export const handlers: Record<string, HandlerFn> = {
  "word.replace_selection": async (args) => {
    const { text } = args ?? {};
    const t = _s(text);
    // Capture the outgoing selection before the swap so the change log can
    // report the edit as before → after. Best-effort: an unreadable selection
    // still logs the replacement text.
    let before = "";
    try {
      before = (await getWordSelection()) ?? "";
    } catch { /* selection unavailable on this host */ }
    // A replacement that produces the SAME text changed nothing, so it must not
    // reach the change log. It was still logged, and /stop-tracking then
    // reported it under "Changes Applied (this session)" as
    //   Replace selection — "…Statement of Work." → "…Statement of Work."
    // with identical text either side of the arrow. The user reads that as an
    // edit surviving after they stopped tracking, when in fact nothing was ever
    // changed. Anything Word DID record is reported separately, from Word's own
    // review state via word_get_review_items, so nothing is lost by skipping it
    // here.
    const unchanged = before.trim() === t.trim();
    const logged = t.trim() && !unchanged;
    // Counted BEFORE the write — see withBaseline.
    const verifier = logged ? await withBaseline(textPresenceVerifier(t, before)) : undefined;
    await replaceWordSelection(t);
    if (logged) {
      recordWordChange(
        "Replaced selection",
        before.trim()
          ? `"${changeSnippet(before)}" → "${changeSnippet(t)}"`
          : `with "${changeSnippet(t)}"`,
        verifier,
      );
    }
    return INSERTED;
  },

  "word.insert_at_cursor": async (args) => {
    const { text } = args ?? {};
    const t = _s(text);
    const verifier = t.trim() ? await withBaseline(textPresenceVerifier(t)) : undefined;
    await insertAtWordCursor(t);
    if (t.trim()) recordWordChange("Inserted at cursor", `"${changeSnippet(t)}"`, verifier);
    return INSERTED;
  },

  // TO-02 — insert a parsed outline, levels intact. Distinct from
  // word.insert_at_cursor because the sections arrive STRUCTURED: the level
  // came from the model's payload rather than being inferred from '#' markers,
  // which is the only way "title" can reach the document as a real Title style.
  "word.insert_outline": async ({ sections }) => {
    const list = (Array.isArray(sections) ? sections : []) as OutlineSectionInput[];
    const usable = list.filter((s) => _s(s?.heading).trim());
    if (usable.length === 0) throw new Error("word.insert_outline requires at least one section");
    await insertWordOutline(usable);
    // Verified by its FIRST heading (AIVA-8024). Recorded without a verifier, an
    // inserted outline counted as present for the life of the task-pane session —
    // verifyWordChangeLog trusts an unverifiable entry — so a Ctrl+Z'd outline
    // still reached the next turn inside the completed-actions block, which tells
    // the model "AIVA has already applied the following … do not offer to do them
    // again". A later /outline in a fresh chat was then answered against work the
    // document no longer contained.
    recordWordChange(
      "Inserted outline",
      `${usable.length} section${usable.length === 1 ? "" : "s"}`,
      textPresenceVerifier(_s(usable[0]?.heading)),
    );
    emitToast({ title: `Outline inserted (${usable.length} sections)`, intent: "success" });
    return { inserted: true, sections: usable.length };
  },

  "word.append_to_end": async (args) => {
    const { text } = args ?? {};
    const t = _s(text);
    const verifier = t.trim() ? await withBaseline(textPresenceVerifier(t)) : undefined;
    await appendToWordEnd(t);
    if (t.trim()) recordWordChange("Appended to end", `"${changeSnippet(t)}"`, verifier);
    return INSERTED;
  },

  // TO-01 — insert one of the standard document frames.
  //
  // Appends rather than inserting at the cursor: a skeleton is a whole document
  // structure, and dropping it mid-paragraph splits whatever the user was
  // writing. The specification says "at the end of the document" for the same
  // reason, and it keeps AC-4's "without disturbing existing content" true by
  // construction rather than by care.
  //
  // Goes through appendToWordEnd, so the '#'/'##' markers become real Word
  // heading styles via the same parseMdBlocks routine every other insertion
  // uses — no second insertion path to keep in step (W41).
  "word.insert_template": async ({ template }) => {
    const { key, label, markdown, sections } = resolveTemplate(template, "word.insert_template");
    await appendToWordEnd(markdown);
    recordWordChange("Inserted template", `${label} — ${sections} sections`);
    emitToast({ title: `Outline inserted (${sections} sections)`, intent: "success" });
    return { inserted: true, template: key, sections };
  },

  "word.apply_review": async ({ changes }) => {
    const changeList = (changes as Array<{ original: string; replacement: string }>) ?? [];
    const { applied, skips } = await applyWordReview(changeList);
    if (applied > 0) {
      const pairs = changeList
        .slice(0, MAX_LISTED_CHANGES)
        .map((c) => `"${changeSnippet(c.original)}" → "${changeSnippet(c.replacement ?? "")}"`)
        .join("; ");
      recordWordChange(
        `Applied ${applied} review change${applied === 1 ? "" : "s"}`,
        pairs + listOverflow(changeList.length),
      );
    }
    // Findings are anchored by an exact `original` phrase. Any whose phrase no
    // longer matches the document (the user edited it, or the LLM paraphrased
    // it) is skipped by applyWordReview — previously in silence, so the pane
    // reported success for changes that never landed. Count the skips against
    // the changes that were actually attempted (an entry with an empty
    // `original` is invalid input, not a failed match) and surface them.
    const attempted = changeList.filter((c) => c?.original).length;
    const skipped = Math.max(0, attempted - applied);

    if (skips.length > 0) {
      recordWordChange(
        `Skipped ${skips.length} review change${skips.length === 1 ? "" : "s"}`,
        skips
          .slice(0, MAX_LISTED_CHANGES)
          .map((s) => `"${changeSnippet(s.original)}" (${SKIP_REASON_LABEL[s.reason]})`)
          .join("; ") + listOverflow(skips.length),
      );
    }
    const message = applyReviewMessage(attempted, applied, skipped, skips);
    return {
      applied,
      skipped,
      attempted,
      ambiguous: countSkips(skips, "ambiguous"),
      tooLong: countSkips(skips, "too_long"),
      ...(message ? { message } : {}),
    };
  },

  // RV-01 — the Apply review button. Comments plus tracked changes, so nothing
  // is silently rewritten and the reviewer keeps the audit trail (AC-2).
  "word.apply_review_edits": async ({ findings }) => {
    const list = (Array.isArray(findings) ? findings : []) as ReviewEditFinding[];
    const result = await executeReviewEdits(list);
    if (result.applied > 0) {
      recordWordChange(
        `Applied ${result.applied} review finding${result.applied === 1 ? "" : "s"}`,
        `${result.applied} comment(s)${result.tracked ? " and tracked change(s)" : ""} across the document`,
      );
      emitToast({
        title: `Applied ${result.applied} finding${result.applied === 1 ? "" : "s"}`,
        intent: "success",
      });
    }
    const message = reviewEditsMessage(result);
    return { ...result, ...(message ? { message } : {}) };
  },

  "word.insert_comment": async ({ phrase, comment, occurrence }) => {
    const p = _s(phrase);
    const c = _s(comment);
    const nth = typeof occurrence === "number" && occurrence > 0 ? occurrence : undefined;
    const result = await insertWordComment(p, c, nth);
    if (result.ok) {
      recordWordChange("Added comment", `on "${changeSnippet(result.anchor ?? p)}": "${changeSnippet(c)}"`);
      return { inserted: true, ok: true, anchor: result.anchor };
    }
    // Every failure names its own cause. Collapsing them into `inserted: false`
    // told the model the phrase was missing even when Word was too old to hold
    // a comment at all, and it would then rephrase the anchor forever.
    return {
      inserted: false,
      ok: false,
      reason: result.reason,
      ...(result.matches ? { matches: result.matches } : {}),
      message: commentFailureMessage(p, result),
    };
  },

  // AC1 — one round trip for a whole review's worth of comments. The per-item
  // results come back in the order given, so a partial failure says which
  // findings landed rather than reporting a single boolean for all of them.
  "word.insert_comments": async ({ comments }) => {
    const requests = (Array.isArray(comments) ? comments : [])
      .map((raw) => {
        const item = (raw ?? {}) as { phrase?: unknown; comment?: unknown; occurrence?: unknown };
        const nth = typeof item.occurrence === "number" && item.occurrence > 0 ? item.occurrence : undefined;
        return { phrase: _s(item.phrase), comment: _s(item.comment), occurrence: nth };
      })
      .filter((r) => r.phrase && r.comment);

    if (requests.length === 0) {
      return { inserted: 0, attempted: 0, results: [], message: "No comments to add." };
    }

    const batch = await insertWordComments(requests);
    const message = batchCommentMessage(batch);
    if (batch.inserted > 0) {
      recordWordChange(
        `Added ${batch.inserted} comment${batch.inserted === 1 ? "" : "s"}`,
        requests
          .filter((_, i) => batch.results[i]?.ok)
          .map((r) => `"${changeSnippet(r.phrase)}"`)
          .join(", "),
      );
      emitToast({
        title: `Added ${batch.inserted} comment${batch.inserted === 1 ? "" : "s"}`,
        intent: "success",
      });
    }
    return {
      ...batch,
      results: batch.results.map((r, i) => ({
        ...r,
        ...(r.ok ? {} : { message: commentFailureMessage(requests[i]?.phrase ?? r.phrase, r) }),
      })),
      ...(message ? { message } : {}),
    };
  },

  // Two ways to centre the window, in descending precision:
  //   anchor_index  — a `toc` entry's startParagraph. Exact.
  //   anchor_phrase — a literal text match. Centres on a TOPIC rather than on the
  //                   cursor, so a draft request can be answered from the right part
  //                   of a long agreement without the user scrolling there (AIVA-7004).
  // Pages are deliberately NOT addressable here: the Word JS API cannot locate one,
  // and a TOC's printed numbers are absent on Word on the web, so every entry came
  // back null. The window is measured in paragraphs — roughly ten to a page.
  "word.get_selection_context": async ({
    paragraphs_before,
    paragraphs_after,
    anchor_phrase,
    anchor_index,
  }) => {
    const index = typeof anchor_index === "number" ? anchor_index : undefined;
    // An ANCHORED call means "read that section", so a missing window size must not
    // collapse to the cursor default of 1 — that reads three paragraphs and drafts
    // from them while looking exactly like a successful run. A bare cursor call
    // keeps 1, which is what the other skills granting this tool expect.
    // Ten paragraphs make roughly a page, so 30 is about three pages.
    const anchored = index !== undefined || _s(anchor_phrase).length > 0;
    const fallback = anchored ? ANCHORED_WINDOW_PARAGRAPHS : 1;
    return getWordSelectionContext(
      typeof paragraphs_before === "number" ? paragraphs_before : fallback,
      typeof paragraphs_after === "number" ? paragraphs_after : fallback,
      _s(anchor_phrase),
      index,
    );
  },

  "word.replace_all": async ({ find, replace, match_case, expected_count }) => {
    const f = _s(find);
    const matchCase = match_case === true;
    const aborted = await replaceAllAbortIfCountMoved(f, matchCase, expected_count);
    if (aborted) return aborted;

    const count = await replaceAllInDocument(f, _s(replace), matchCase);
    if (count > 0) {
      recordWordChange(
        "Replaced all",
        `"${changeSnippet(f)}" → "${changeSnippet(_s(replace))}" (${count} occurrence${count === 1 ? "" : "s"}${matchCase ? ", case-sensitive" : ""})`,
      );
    }
    // A document-wide swap that matched nothing looks identical to one that
    // worked — both just settle the card. Say so explicitly (see the `message`
    // note on word.apply_review for how this reaches the chat).
    return {
      count,
      ...(count === 0
        ? {
            message:
              `No occurrences of “${changeSnippet(f)}” were found${matchCase ? " with matching case" : ""} — ` +
              "nothing was changed.",
          }
        : {}),
    };
  },

  // DP-05 — paste an email into the document. Fires only from a pill the user
  // clicked, and the body it writes is the body that pill was built with: this
  // handler never searches, so there is no path by which a different message
  // than the one named on the card can be inserted (G-17).
  "word.insert_email": async ({ preface, body, subject, sender, email_id, mailbox }, ctx?: HandlerContext) => {
    const emailBody = _s(body);
    if (!emailBody.trim()) throw new Error("word.insert_email requires a non-empty body");
    const subjectText = _s(subject) || "(no subject)";
    const senderText = _s(sender);
    // Built once: the sender is optional, and every line below that names the
    // message has to read the same way whether or not one was resolved.
    const fromSuffix = senderText ? ` from ${senderText}` : "";

    const images = await resolveEmailImages(emailBody, _s(email_id), _s(mailbox) || null, ctx);
    const result = await insertWordEmailAtCursor(_s(preface), emailBody, { images });
    if (!result.inserted) {
      return {
        inserted: false,
        reason: result.reason,
        message:
          "The email could not be fully inserted — Word rejected some of its content, or the document is " +
          "protected. Any part that was written can be removed with Ctrl+Z.",
      };
    }
    recordWordChange("Pasted email", `${changeSnippet(subjectText)}${fromSuffix}`, textPresenceVerifier(emailBody));
    emitToast({
      title: `Inserted “${subjectText}”${fromSuffix}`,
      intent: "success",
    });
    const caveat = result.formattingSkipped
      ? " Some of its links, pictures or list formatting could not be applied on this version of Word."
      : "";
    return {
      inserted: true,
      paragraphs: result.paragraphs,
      ...(result.formattingSkipped ? { formatting_skipped: true } : {}),
      message: `Inserted the body of “${subjectText}”${fromSuffix} at the cursor.${caveat}`,
    };
  },

  // FS-02 — strip all styles. The most destructive operation in the channel, so
  // it is split in two: this emitter only COUNTS and offers, the handler below
  // only applies what was offered (G-6 closure).
  //
  // A selection changes the blast radius: with text selected, "strip styles"
  // must act on only that selection, not the whole document. Selection wins
  // whenever there is one — it's a scope the user just deliberately set.
  "ui.pill.word.strip_styles": async () => {
    const selectionParagraphs = await countWordSelectionParagraphs();
    if (selectionParagraphs > 0) {
      emitPills([
        {
          type: "office_action",
          label: `Strip styles from selection (${selectionParagraphs})`,
          handler: "word.strip_selection_styles",
          args: {
            expected_count: selectionParagraphs,
            impact_line:
              `This will remove direct formatting — font, color, bold/italic, highlight — from the ` +
              `${selectionParagraphs} selected paragraph${selectionParagraphs === 1 ? "" : "s"}. Only Ctrl+Z restores it.`,
          },
        },
      ]);
      return { rendered: true, scope: "selection", paragraphs: selectionParagraphs };
    }

    // Surveys rather than counts (AIVA-7613). Counting paragraphs was all this
    // ever did, so an already-plain document still got an approval card
    // promising to reset every paragraph — for an operation that would change
    // nothing. Offering a destructive-sounding button for a no-op is its own
    // defect; the card has to mean something.
    const { paragraphs, wouldChange } = await surveyWordFormatting();
    if (paragraphs === 0) {
      return { rendered: false, paragraphs: 0, message: "The document has no paragraphs to reset." };
    }
    if (!wouldChange) {
      return {
        rendered: false,
        paragraphs,
        formatted: false,
        message:
          "This document has no formatting to strip — every paragraph is already Normal "
          + "(Calibri 11, black), so stripping would change nothing.",
      };
    }
    emitPills([
      {
        type: "office_action",
        label: `Strip all styles (${paragraphs})`,
        handler: "word.strip_styles",
        args: {
          expected_count: paragraphs,
          // Rendered verbatim by WordPillApprovalCard — the blast radius has to
          // be on screen before the click, not described after it.
          impact_line:
            `This will reset ${paragraphs} paragraph${paragraphs === 1 ? "" : "s"} to Normal ` +
            `(Calibri 11, black), including any house styles. Only Ctrl+Z restores them.`,
        },
      },
    ]);
    return { rendered: true, scope: "document", paragraphs };
  },

  "word.strip_styles": async ({ expected_count }) => {
    const expected = typeof expected_count === "number" ? expected_count : undefined;
    const result = await stripAllWordStyles(expected);

    if (result.stripped) {
      recordWordChange("Stripped all styles", `${result.paragraphs} paragraph(s) reset to Normal`);
      emitToast({ title: "Stripped all styles", intent: "success" });
      return {
        stripped: true,
        paragraphs: result.paragraphs,
        message: `Reset ${result.paragraphs} paragraph(s) to Normal.`,
      };
    }
    // Each refusal says what to do next: an approval for 247 paragraphs is not
    // an approval for 260, so the user re-asks rather than being surprised.
    const messages: Record<string, string> = {
      empty: "The document has no paragraphs to reset — nothing was changed.",
      count_moved:
        `The document changed since you approved this — it now has ${result.actual} paragraphs, ` +
        `not ${result.expected}. Nothing was changed; ask again to see the current count.`,
      failed: "The styles could not be reset — the document may be protected or read-only. Nothing was changed.",
    };
    return { stripped: false, reason: result.reason, message: messages[result.reason ?? "failed"] };
  },

  // Strip styles scoped to the current selection only — never touches
  // paragraph.style, so a partial-paragraph selection can't restyle text
  // the user didn't select (see stripSelectionStyles).
  "word.strip_selection_styles": async ({ expected_count }) => {
    const expected = typeof expected_count === "number" ? expected_count : undefined;
    const result = await stripSelectionStyles(expected);

    if (result.stripped) {
      recordWordChange("Stripped styles from selection", `${result.paragraphs} paragraph(s) reset to plain formatting`);
      emitToast({ title: "Stripped styles from selection", intent: "success" });
      return {
        stripped: true,
        paragraphs: result.paragraphs,
        message: `Removed formatting from ${result.paragraphs} selected paragraph(s).`,
      };
    }
    const messages: Record<string, string> = {
      empty: "Nothing is selected — nothing was changed.",
      count_moved:
        "The selection changed since you approved this — nothing was changed. Ask again to see the current selection.",
      failed: "The formatting could not be cleared — the document may be protected or read-only. Nothing was changed.",
    };
    return { stripped: false, reason: result.reason, message: messages[result.reason ?? "failed"] };
  },

  // RV-02 — list comments. Fully local read; the card renders the rows and owns
  // the per-row Resolve / Delete actions.
  "word.list_comments": async () => {
    const comments = await getWordComments();
    if (comments === null) {
      return {
        comments: [],
        supported: false,
        message: "AiVA can't reach comments in this version of Word. Open the document in the Word desktop app.",
      };
    }
    if (comments.length === 0) {
      return { comments: [], supported: true, message: "No comments in this document." };
    }
    const open = comments.filter((c) => !c.resolved).length;
    return {
      comments,
      supported: true,
      total: comments.length,
      open,
      message: `${comments.length} comment${comments.length === 1 ? "" : "s"} in this document.`,
    };
  },

  "word.resolve_comment": async ({ comment_id }) => {
    const id = _s(comment_id);
    const result = await resolveWordComment(id);
    if (result.ok) {
      recordWordChange("Resolved comment", id, { kind: "comment", id, resolved: true });
      // RV-02 specifies a toast per row action — the row fade alone is easy to
      // miss when the card has several rows.
      emitToast({ title: "Comment resolved", intent: "success" });
      return { ok: true, message: "Comment resolved." };
    }
    return {
      ok: false,
      reason: result.reason,
      message:
        result.reason === "not_found"
          ? "That comment is no longer in the document."
          : "The comment could not be resolved.",
    };
  },

  // Irreversible in Word, so the card requires a second click before this runs
  // (G-8). Nothing here re-confirms — the handler does what it is told.
  "word.delete_comment": async ({ comment_id }) => {
    const id = _s(comment_id);
    const result = await deleteWordComment(id);
    if (result.ok) {
      recordWordChange("Deleted comment", id);
      emitToast({ title: "Comment deleted", intent: "success" });
      return { ok: true, message: "Comment deleted." };
    }
    return {
      ok: false,
      reason: result.reason,
      message:
        result.reason === "not_found"
          ? "That comment is no longer in the document."
          : "The comment could not be deleted.",
    };
  },

  // RV-04 — accept / reject all tracked changes. Document-wide and destructive,
  // so the count is offered first (G-10 closure) exactly as FS-02 does.
  "ui.pill.word.review_changes": async ({ mode }) => {
    const count = await countWordTrackedChanges();
    if (count === null) {
      return {
        rendered: false,
        supported: false,
        message: "AiVA can't reach tracked changes in this version of Word. Open the document in the Word desktop app.",
      };
    }
    if (count === 0) {
      return { rendered: false, supported: true, count: 0, message: "No tracked changes in this document." };
    }
    // The model may ask for one or leave it open; offering both is safer than
    // guessing, because accepting and rejecting are opposite outcomes.
    const wanted = mode === "accept" || mode === "reject" ? [mode] : ["accept", "reject"];
    const plural = count === 1 ? "" : "s";
    emitPills(
      wanted.map((m) => ({
        type: "office_action" as const,
        label: `${m === "accept" ? "Accept" : "Reject"} all ${count}`,
        handler: m === "accept" ? "word.accept_changes" : "word.reject_changes",
        args: {
          expected_count: count,
          impact_line:
            m === "accept"
              ? `This will accept ${count} tracked change${plural}, making every edit permanent.`
              : `This will reject ${count} tracked change${plural}, discarding every edit in the document.`,
        },
      })),
    );
    return { rendered: true, count };
  },

  "word.accept_changes": async ({ expected_count }) => applyTrackedChanges("accept", expected_count),
  "word.reject_changes": async ({ expected_count }) => applyTrackedChanges("reject", expected_count),

  "word.highlight_text": async ({ phrase, color }) => {
    const count = await highlightWordText(_s(phrase), _s(color) || "Yellow");
    return { count };
  },

  "word.clear_highlights": async ({ ids, turn }) => {
    const idList = Array.isArray(ids) ? ids.filter((id): id is number => typeof id === "number") : undefined;
    const turnNo = typeof turn === "number" ? turn : undefined;
    if (await clearWordHighlights(idList, turnNo)) return { cleared: true };
    return {
      cleared: false,
      reason: "not tracked",
      message:
        "These highlights aren't tracked any more — they were applied before the task pane was reloaded. " +
        "Re-run the command to highlight them again, or select the text and set Word's own Text Highlight Colour to No Color.",
    };
  },

  "word.get_document_structure": async () => {
    // TWO document reads, not four. `readWordDocumentMap` derives the table of
    // contents, the flat heading list and the content counts from ONE paragraph
    // sweep; the TOC field read is the only other round trip and it is small.
    // Four separate readers — two of them sweeping every paragraph independently —
    // is what timed this tool out at 5s on a 1,300-paragraph agreement.
    //
    // `toc` is what the caller works from, and it is populated wherever the
    // document has headings at all:
    //   - The document has its own TOC field -> read it, and report `source: "toc"`.
    //     Its page numbers are NOT merged onto the sections, deliberately: that
    //     route was built and removed (spec DE-01, docs/word-write-toc-approach.md
    //     "Why page numbers are gone"). `page` came back null on every entry of
    //     every real test document — Word on the web does not paginate, and an
    //     AIVA-inserted TOC uses the `\z` switch, which suppresses page numbers.
    //     And even resolved it only ever became a paragraph index, which
    //     `startParagraph` below already gives exactly. `resolveTocPageToParagraph`
    //     and `mergeTocPages` are in git history if it is ever revived.
    //   - It has none -> the equivalent is built from the heading styles, in
    //     memory. Nothing is written to the document: no field, no "Contents"
    //     heading, no undo entry. `source: "headings"`.
    //
    // Entries are ADDRESSABLE — `startParagraph` indexes body.paragraphs — which is
    // what lets a window be taken exactly rather than by matching a phrase.
    const fieldRead = await getWordTableOfContents();
    // Array.isArray, not `?? null`: a host (or a stub) that answers with undefined
    // must read as "unknown" like a refused read does, rather than throwing partway
    // through a read-only call.
    const fromFields = Array.isArray(fieldRead) ? fieldRead : null;
    const map = (await readWordDocumentMap()) ?? {
      toc: [],
      headings: [],
      tocParagraphs: [],
      provenance: { styleBacked: 0, outlineLevelOnly: 0 },
      paragraphCount: 0,
      wordCount: 0,
      readOk: false,
    };
    // `body.fields` is the direct route to the contents page but not a dependable
    // one: on Word on the web it returns EMPTY rather than throwing, which had a
    // document carrying a real TOC reporting hasToc:false and losing every page
    // number with it. The TOC's own paragraph styles say the same thing and come
    // free with the sweep, so they stand in whenever the fields read finds nothing.
    let fieldToc = fromFields;
    const fieldsReadEmpty = fromFields === null || fromFields.length === 0;
    if (fieldsReadEmpty && map.tocParagraphs.length > 0) {
      fieldToc = map.tocParagraphs;
    }
    let source: "toc" | "headings" | "none" = "none";
    if (fieldToc !== null && fieldToc.length > 0) {
      source = "toc";
    } else if (map.headings.length > 0) {
      source = "headings";
    }
    // The outline, RENDERED HERE rather than transcribed by the model.
    //
    // The prompt used to ask for the whole heading list twice — once as markdown
    // in chat, once again as the pill's payload. On a 171-heading document that
    // is ~5,000 tokens of pure copying on the fast tier, and it stopped at #71:
    // the user saw 71 of 171 headings with no sign that anything was missing.
    // Both renderings now come from `toc`, so there is no transcription left to
    // truncate and the bubble, Insert and Copy cannot disagree.
    const outlineRows = outlineFromToc(map.toc);
    // AIVA-8024 — WHICH JOB /outline is doing, decided HERE rather than inferred
    // by the model from how many headings it can see.
    //
    // Two failures, one cause. `readOk: false` and a genuinely unstructured
    // document both arrive as zero headings, so a REFUSED read was
    // indistinguishable from "no structure yet" and /outline answered a document
    // that has 171 headings with an invented skeleton. And because the model
    // re-derived the mode every turn, the same unchanged document could land in
    // either mode on consecutive runs: the skill samples at the deployment's
    // default temperature, so the inference was re-rolled each time and "run
    // /outline twice, get two different answers" followed. Pinning the skill's
    // temperature helps but cannot be relied on — llm_factory drops the kwarg
    // for the GPT-5.x deployments, which reject an explicit value — so the
    // decision has to stop being a judgement call at all.
    //
    // Keyed on `outlineRows`, not `map.headings`: the rows are what the add-in
    // renders as the outline card, and READ MODE's prompt tells the model the
    // outline is ALREADY ON SCREEN. Deciding off a different list is how that
    // claim came to be made on a turn where no card had rendered.
    //
    // "unavailable" is NOT "build". There is nothing to say about a document that
    // could not be read, and generating an outline for one is the bug rather than
    // a graceful degradation.
    let mode: "read" | "build" | "unavailable";
    if (!map.readOk) {
      mode = "unavailable";
    } else if (outlineRows.length > 0) {
      mode = "read";
    } else {
      mode = "build";
    }
    // Hand this sweep to READ MODE's insert pill so it does not take its own —
    // see `cacheOutlineToc`. Stored after `mode` is settled and keyed on the same
    // `readOk`, so the pill can never be handed a toc from a read this handler
    // just reported as "unavailable".
    cacheOutlineToc(map.toc, map.readOk);
    return {
      source,
      mode,
      // null = unknown (the read was refused), NOT false. Reporting "this document
      // has no table of contents" off a failed read is the mistake this keeps out.
      hasToc: fieldToc === null ? null : fieldToc.length > 0,
      toc: map.toc,
      headings: map.headings,
      outline: {
        rows: outlineRows,
        // Markdown only. The clipboard string is NOT carried here: Copy is the
        // action bar's pill, built from the same sections as Insert, so a second
        // rendering of the same outline would be a second thing to disagree.
        markdown: outlineToMarkdown(outlineRows),
      },
      // What the model should SAY, as opposed to re-list. `styleBacked: 0` with a
      // non-zero `outlineLevelOnly` is the fact that explains why such a document
      // misbehaves everywhere else in the add-in, and it was being thrown away.
      health: {
        total: map.headings.length,
        minLevel: map.toc.length > 0 ? Math.min(...map.toc.map((e) => e.level)) : null,
        maxLevel: map.toc.length > 0 ? Math.max(...map.toc.map((e) => e.level)) : null,
        depth: outlineRows.length > 0 ? Math.max(...outlineRows.map((r) => r.level)) : 0,
        // Zeros are the safe default, not a guess: `provenanceNote` speaks only
        // when outlineLevelOnly > 0, so an absent provenance produces SILENCE
        // about how the headings are recorded rather than a false "none carry
        // heading styles". Same discipline as hasToc:null above.
        styleBacked: map.provenance?.styleBacked ?? 0,
        outlineLevelOnly: map.provenance?.outlineLevelOnly ?? 0,
      },
      // The outline's shape, worked out here rather than by the model. On a long
      // repeating agreement this is the answer the user wants, and it is the part
      // that was being derived wrongly — same name counted twice because the
      // numbering differs, lists that trailed off in "...".
      shape: summariseHeadingShape(map.headings),
      paragraphCount: map.paragraphCount,
      wordCount: map.wordCount,
      readOk: map.readOk,
    };
  },

  // AIVA-7018 — read a window of the document by index. The counterpart to
  // word_get_document_structure: that returns WHERE each section is, this returns
  // what is there. Together they let a 1,200-page agreement be read a section at a
  // time instead of by attaching its opening to every turn.
  "word.read_paragraphs": async ({ start, count }) => {
    const from = typeof start === "number" ? start : 0;
    const take = typeof count === "number" ? count : 10;
    const window = await readWordParagraphs(from, take);
    if (window === null) {
      return { readOk: false, message: "AiVA couldn't read that part of the document." };
    }
    if (window.paragraphs.length === 0) {
      return {
        readOk: true,
        ...window,
        message: `The document has ${window.total} paragraphs, so there is nothing at index ${from}.`,
      };
    }
    return { readOk: true, ...window };
  },

  "word.get_format_candidates": async ({ startIndex }) => {
    // Read-only (AIVA-8606). One sweep of EVERY paragraph — not the first 6,000
    // characters the chat context carries — returning every paragraph short
    // enough to be a heading, with its body.paragraphs index and the facts the
    // model judges it by. /format chooses styles from this list and applies them
    // through pill_word_apply_styles; no text is retyped, so nothing past the
    // context cap can be lost.
    // A long document is read in pages: `startIndex` is the previous page's
    // `nextIndex`, so every paragraph is reachable in one turn.
    return getWordFormatCandidates(typeof startIndex === "number" ? startIndex : 0);
  },

  "word.get_review_items": async () => {
    // Read-only: native tracked revisions + margin comments for /changes,
    // scoped to THIS pane session — older revisions still sitting in the
    // document (never accepted/rejected) are not re-reported every session.
    const items = await getWordReviewItems(getSessionStartMs());
    // AIVA-8048: and to what no earlier /stop-tracking report already showed
    // — see beginChangeReport. A revision or comment has no stable id, so it
    // is recognised by what the report prints about it; a comment resolved
    // since it was reported is a new change and is shown again.
    return {
      ...items,
      trackedChanges: unreportedReviewItems(
        items.trackedChanges,
        (c) => `revision\u0000${c.type}\u0000${c.author}\u0000${c.date}\u0000${c.text}`,
      ),
      comments: unreportedReviewItems(
        items.comments,
        (c) => `comment\u0000${c.author}\u0000${c.date}\u0000${c.text}\u0000${c.resolved}`,
      ),
    };
  },

  "word.set_tracking": async ({ enabled }) => {
    // Not a pill — executes immediately mid-turn (word_set_tracking tool),
    // driven by the /track and /stop slash commands. The mode persists with
    // the document, so tracking stays on until /stop flips it back.
    const on = enabled === true;
    const result = await setWordChangeTracking(on);
    if (result.ok && result.changed) {
      recordWordChange(
        on ? "Started tracking changes" : "Stopped tracking changes",
        on
          ? "Word Track Changes turned on — edits are now marked as revisions"
          : "Word Track Changes turned off",
        { kind: "tracking", enabled: on },
      );
    }
    if (!result.ok) {
      return {
        ...result,
        message:
          result.reason === "unsupported"
            ? "This version of Word can't turn Track Changes on or off from an add-in (it needs WordApi 1.4). " +
              "Use Word's Review tab instead."
            : "Track Changes could not be changed. The document may be protected.",
      };
    }
    return result;
  },

  "word.insert_toc": async ({ changes }) => {
    // One click does both: restructure the heading styles (when the skill
    // passed a restyle list) THEN insert the native TOC field, so the TOC
    // reflects the inferred hierarchy immediately.
    const changeList = (Array.isArray(changes) ? changes : []) as Array<{ text: string; style: string }>;
    let styled = 0;
    if (changeList.length > 0) {
      styled = await applyWordStyles(changeList);
      if (styled > 0) {
        recordWordChange(
          "Applied formatting",
          `${styled} heading${styled === 1 ? "" : "s"} restructured for the table of contents`,
        );
      }
    }
    const result = await insertWordTOC();
    if (result.inserted) {
      recordWordChange(
        "Inserted table of contents",
        "native Word Contents field with automatic page numbers",
        { kind: "toc" },
      );
    }
    // A heading whose text no longer matches the document exactly is skipped by
    // applyWordStyles without a word, and the TOC then silently omits that
    // section — the user sees a Contents list with entries missing and no
    // explanation. Report it instead. (Derived, so applyWordStyles' signature
    // and its dozen callers/tests stay untouched.)
    const skipped = changeList.length - styled;
    let message: string | undefined;
    if (!result.inserted) {
      message =
        "I couldn't insert the Contents field in this version of Word. The heading styles were still applied, so inserting a table of contents from Word's References tab will now work.";
    } else if (skipped > 0) {
      message =
        `Contents inserted, but ${skipped} of ${changeList.length} heading${changeList.length === 1 ? "" : "s"} ` +
        "could not be located in the document, so those sections may be missing from the list. " +
        "The wording may have changed since the outline was read.";
    } else if (result.stalePageNumbers) {
      message =
        "Contents inserted, but Word couldn't recalculate its page numbers, so they may be too low. " +
        "Right-click the table of contents and choose Update Field → Update page numbers only.";
    }
    return { ...result, styled, skipped, attempted: changeList.length, ...(message ? { message } : {}) };
  },

  // -------------------------------------------------------------------------
  // Bates numbering (DP-02)
  // -------------------------------------------------------------------------

  "word.insert_bates": async (args) => {
    const config = normaliseBatesConfig(args ?? {});
    const stamp = formatBatesStamp(config);

    if (!supportsFieldInsertion()) {
      return {
        applied: false,
        sections: 0,
        reason: "unsupported_host",
        message:
          "This version of Word can't insert the numbering field, so the document is untouched. Bates numbering " +
          "has to be a live field — a typed number would be wrong the moment a page is added — so there is no " +
          "safe fallback I can apply here.",
      };
    }

    const result = await insertWordBates(config);

    // G-13: the offset was attempted as an OOXML calculated field and the
    // read-back showed this host rendered it as code (or rejected the package)
    // — the stamp was removed, so the document carries neither a wrong number
    // nor a visible error. AC-6: the user is told, with the two real options.
    if (result.reason === "offset_unsupported") {
      return {
        ...result,
        message:
          `I tried to start Bates numbering at ${config.startAt}, but this version of Word couldn't render the ` +
          `calculated field the offset needs, so I removed the stamp — the document is unchanged. A bundle ` +
          `numbered from 1 when it should start at ${config.startAt} would be a citation error, so I won't stamp ` +
          `that silently. I can stamp ${config.prefix}${"0".repeat(Math.max(0, config.padding - 1))}1 onwards ` +
          `instead, or set the start value in Word: Insert → Page Number → Format Page Numbers → Start at.`,
      };
    }
    if (!result.applied) {
      return {
        ...result,
        message: `I couldn't stamp the ${config.position}. The document is unchanged.`,
      };
    }

    const placement = describeBatesPlacement(config);
    recordWordChange(
      "Applied Bates numbering",
      `${stamp} onwards in the ${config.position} (${placement}) of ${result.sections} ` +
        `section${result.sections === 1 ? "" : "s"} ` +
        `(prefix "${config.prefix}", padding ${config.padding}, starting at ${config.startAt})`,
      { kind: "bates", prefix: config.prefix, position: config.position },
    );
    const base =
      `Stamped ${stamp} onwards in the ${config.position} (${placement}) of ${result.sections} ` +
      `section${result.sections === 1 ? "" : "s"}. Existing ${config.position} content was kept.`;
    return {
      ...result,
      // verified === false means the field went in but we could not confirm what
      // Word rendered — either it hides field.result, or `= PAGE + n` had not
      // been paginated yet when we looked. The stamp is KEPT and the user is
      // told to check, which is what G-13's AC-6 asks for. Claiming a verified
      // stamp on a court production would be worse; so would deleting a correct
      // one on a guess.
      message:
        result.verified === false
          ? `${base} I couldn't confirm how this version of Word rendered the start offset — ` +
            `check page 1 shows ${stamp} and not field code before producing this bundle.`
          : base,
    };
  },

  "word.remove_bates": async (args) => {
    // Deliberately NOT normaliseBatesConfig: its WL- default belongs to
    // APPLYING a stamp (DP-02's configuration grammar). On removal, no prefix
    // means "every Bates stamp, whatever its prefix" (AC-4), and no position
    // means both footer and header are swept.
    const prefix = readBatesRemovalPrefix(args);
    const position = readRemovalRegion(args);
    const result = await removeWordBates(prefix, position);
    const where = position ?? "footer and header";
    const scope = prefix ? `“${prefix}…”` : "Bates";
    if (!result.applied) {
      let message = `I found no ${scope} stamps in the ${where} to remove.`;
      if (prefix) {
        // A scoped miss is often not the user's scoping at all — a stale model
        // config defaults the prefix to WL- — so a bare "no WL- stamps" reads
        // as "removal is broken" when the page visibly carries SIM-500. Name
        // what IS there and how to remove it, instead of a dead end.
        const regions = removalRegions(position);
        const { stamps } = await surveyBatesRemovalTargets(regions, undefined);
        if (stamps.length > 0) {
          message +=
            ` The ${where} does hold ${stamps.slice(0, 4).join(", ")}${stamps.length > 4 ? ", …" : ""} — ` +
            `ask me to "remove all bates numbering" to clear every series, or name that prefix.`;
        }
      }
      return { ...result, message };
    }
    recordWordChange("Removed Bates numbering", `${where} of ${result.sections} section(s)`);
    return { ...result, message: `Removed the ${prefix ?? "Bates"} numbering from the ${where}.` };
  },


  // -------------------------------------------------------------------------
  // Hyperlink (DP-04)
  // -------------------------------------------------------------------------

  "word.insert_hyperlink": async (args) => {
    const rawUrl = _s(args?.url);
    const display = _s(args?.text);
    const result = await insertWordHyperlink(rawUrl, display);
    if (result.reason === "invalid_url") {
      return {
        ...result,
        message:
          `“${changeSnippet(rawUrl)}” isn't a web address I can link to. I only write http and https links — ` +
          "a script or data link in a document someone else opens is a security risk, not a convenience.",
      };
    }
    if (result.reason === "unsupported_host") {
      return {
        ...result,
        message:
          "This version of Word can't set hyperlinks from an add-in (it needs WordApi 1.3), so nothing was " +
          "inserted. Paste the address and use Word's Insert → Link instead.",
      };
    }
    if (!result.inserted) {
      return { ...result, message: "I couldn't insert the link. The document is unchanged." };
    }
    recordWordChange("Inserted hyperlink", `"${changeSnippet(result.text ?? "")}" → ${result.url}`);
    return { ...result, message: `Inserted a link on “${result.text}” pointing at ${result.url}.` };
  },


  // Page numbers (DP-03)
  // -------------------------------------------------------------------------

  "word.insert_page_numbers": async (args) => {
    // Self-guard: this handler must never service a Bates-numbering request.
    // Bates is a separate capability (prefix + padding + start offset, litigation
    // production semantics), so silently treating a Bates prompt as "page
    // numbers" would stamp the wrong thing into a court bundle. Refuse and name
    // the right capability — DP-02 now implements it as `word.insert_bates`.
    if (hasBatesIntent(args)) {
      return {
        applied: false,
        sections: 0,
        message:
          "That looks like a Bates-numbering request — a prefix, zero padding and a start offset — which is a " +
          "different capability from plain page numbers, and I won't stamp the wrong one into a production bundle. " +
          "Ask me to apply Bates numbering instead and I'll stamp the prefix, padding and start value you want.",
      };
    }

    const position: PageNumberPosition = _s(args?.position) === "header" ? "header" : "footer";
    const alignment = readPageNumberAlignment(args);
    // The plan resolves what a skip run can actually deliver — and drops a
    // custom start when one rode along with it, because the two cannot both be
    // honoured. Read through it, never past it.
    const plan = planFrontMatterSkip(
      args,
      _s(args?.format) === "with_total" ? "with_total" : "simple",
      pageNumeralStyle(args),
      pageNumberStartAt(args),
    );
    const { format, numeralStyle, startAt, skipFrontMatter } = plan;

    if (!supportsFieldInsertion()) {
      return {
        applied: false,
        sections: 0,
        reason: "unsupported_host",
        message:
          "This version of Word can't insert page-number fields, so I've left the document untouched. " +
          "Page numbers need a live field (Word recalculates them as pages change) — you can add them from " +
          "Word's Insert → Page Number menu.",
      };
    }

    const result = await insertWordPageNumbers(
      format,
      position,
      alignment,
      numeralStyle,
      startAt,
      skipFrontMatter,
    );
    const numeralNote = numeralStyleNote(numeralStyle);
    const startNote = pageNumberStartNote(startAt);
    const shape = format === "with_total" ? "page X of Y" : "page number";
    const plural = sectionCount;
    // Named only when the user moved it. Saying "centred" on every default run
    // is noise, but staying silent about an explicit left/right would leave the
    // user unable to tell a honoured request from an ignored one — which is the
    // bug this fixes (AIVA-7862).
    const alignNote = alignment === "center" ? "" : `, ${alignment}-aligned`;

    if (!result.applied) {
      return { ...result, message: pageNumberFailureMessage(result, position, startAt, skipFrontMatter) };
    }

    // Sections sharing one footer ("Link to Previous") are written once, so the
    // count reported is regions stamped — not the document's section count.
    const skippedVerb = result.skipped === 1 ? "it was" : "they were";
    const skipNote = result.skipped
      ? ` ${plural(result.skipped)} shared that ${position} or already had a number, so ${skippedVerb} left alone.`
      : "";
    const frontMatter = frontMatterNote(plan);
    recordWordChange(
      "Added page numbers",
      `${shape}${numeralNote}${startNote} field in the ${position} of ${plural(result.sections)}${alignNote}` +
        (skipFrontMatter ? ", contents pages left blank" : ""),
      { kind: "pageNumbers", position },
    );
    return {
      ...result,
      alignment,
      startAt,
      skipFrontMatter,
      message:
        `Added ${shape}${numeralNote}${startNote} to the ${position} of ${plural(result.sections)}${alignNote}.${skipNote}` +
        `${frontMatter} Existing ${position} content was kept.`,
    };
  },

  "word.remove_page_numbers": async (args) => {
    // An unnamed region means BOTH — see readRemovalRegion. Sweeping only the
    // footer is what made "remove the page numbers" a silent no-op on a
    // header-numbered document (AIVA-7938).
    const position = readRemovalRegion(args);
    const regions = removalRegions(position);
    const label = regionLabel(position);

    let applied = false;
    let sections = 0;
    const cleared: PageNumberPosition[] = [];
    for (const region of regions) {
      const outcome = await removeWordPageNumbers(region); // NOSONAR - header and footer edits must apply in order, one Word.run at a time
      if (outcome.applied) {
        applied = true;
        cleared.push(region);
        sections = Math.max(sections, outcome.sections ?? 0);
      }
    }

    if (!applied) {
      return {
        applied: false,
        sections,
        message: `There were no page numbers to remove from the ${label}.`,
      };
    }
    const removedFrom = cleared.join(" and ");
    recordWordChange("Removed page numbers", `${removedFrom} of ${sections} section(s)`);
    return { applied, sections, message: `Removed the page numbers from the ${removedFrom}.` };
  },

  /**
   * Does the document CARRY page numbers right now? (AIVA-8147)
   *
   * A live read of the document, not a read of what AIVA remembers doing. The
   * skill had no way to look, so "is there a page number in this document?" was
   * answered from the chat transcript: a number the user had just undone with
   * Ctrl+Z was still reported as present, because an earlier message said AIVA
   * had added one.
   *
   * The session change log cannot fix that on its own — it only knows what AIVA
   * itself applied, so a number the document already had, or one the user typed
   * into the footer in Word, is invisible to it in both directions. Reading the
   * document answers all four cases with one mechanism.
   *
   * An unnamed region means BOTH, for the reason `readRemovalRegion` widens
   * rather than narrows: a user asking "are there page numbers?" has not said
   * where, and answering "no" off the footer alone would mis-report every
   * header-numbered document.
   *
   * Read-only. It records nothing in the change log — there is no change.
   */
  "word.check_page_numbers": async (args) => {
    const checked = removalRegions(readRemovalRegion(args));
    const locations: PageNumberPosition[] = [];
    const unreadable: PageNumberPosition[] = [];

    for (const region of checked) {
      const has = await hasWordPageNumbers(region); // NOSONAR - header and footer reads stay ordered, one Word.run at a time
      if (has === null) unreadable.push(region);
      else if (has) locations.push(region);
    }

    return {
      present: locations.length > 0,
      locations,
      checked: [...checked],
      unreadable,
      message: checkPageNumbersMessage(locations, checked, unreadable),
    };
  },


  "word.apply_styles": async ({ changes }) => {
    const changeList = (Array.isArray(changes) ? changes : []) as WordStyleChange[];
    const applied = await applyWordStyles(changeList);
    if (applied > 0) {
      recordWordChange(
        "Applied formatting",
        `${applied} paragraph${applied === 1 ? "" : "s"} restyled (${changeList
          .slice(0, 5)
          .map((c) => `"${changeSnippet(c.text)}" → ${c.style}`)
          .join("; ")})`,
      );
    }
    return { applied };
  },

  "word.insert_table": async ({ headers, rows, insert_position }) => {
    const h = (Array.isArray(headers) ? headers : []).map(String);
    const r = (Array.isArray(rows) ? rows : []).map((row) =>
      (Array.isArray(row) ? row : []).map(String),
    );
    const pos = resolveTableInsertPosition(insert_position);
    const result = await insertWordTable(h, r, pos);
    if (result.inserted) {
      const cellKey = [...h, ...r.flat()].find((c) => c.trim());
      recordWordChange(
        "Inserted table",
        `${result.rows} row${result.rows === 1 ? "" : "s"} × ${result.cols} column${result.cols === 1 ? "" : "s"} (${pos.replace(/_/g, " ")}), headers: ${changeSnippet(h.join(", "))}`,
        cellKey ? textPresenceVerifier(cellKey) : undefined,
      );
    }
    return result;
  },

  // ---------------------------------------------------------------------------
  // Pill emission handlers — one per pill_word_* tool in ToolRegistry.
  // Each handler emits a single office_action pill carrying the content the
  // LLM generated. The user clicks the pill to apply it to the document.
  // ---------------------------------------------------------------------------

  "ui.pill.word.replace_selection": async ({ text }) =>
    await emitTextPill("pill_word_replace_selection", "word.replace_selection", "Replace selection", text),

  "ui.pill.word.insert_at_cursor": async ({ text }) =>
    await emitTextPill("pill_word_insert_at_cursor", "word.insert_at_cursor", "Insert at cursor", text),

  "ui.pill.word.append_to_end": async ({ text }) =>
    await emitTextPill("pill_word_append_to_end", "word.append_to_end", "Append to end", text),

  // TO-01 — one approval pill naming the frame and its section count.
  //
  // The pill carries only the KEY, not the skeleton text. The frame lives in the
  // add-in, so the model cannot paraphrase it on the way through: "the
  // recognised frame, not an improvised one" holds structurally rather than by
  // instruction. It also keeps the tool call tiny, which is the difference
  // between a call the model makes reliably and one it quietly skips.
  "ui.pill.word.insert_template": asPromise(({ template }) => {
    const { key, label, markdown, sections } = resolveTemplate(template, "pill_word_insert_template");
    emitPills([
      {
        type: "office_action",
        label: `Insert ${label} template (${sections})`,
        handler: "word.insert_template",
        args: {
          template: key,
          // Read verbatim by WordPillApprovalCard — the user sees where it lands
          // and how much, before committing.
          impact_line: `Appending the standard ${label} frame — ${sections} sections with real Word heading styles — to the end of the document.`,
        },
      },
      // Copy, per the Word action-bar spec: outline and template replies carry a
      // renamed placement button plus Copy, so the frame can go into a document
      // AiVA is not attached to.
      //
      // The text is read from WORD_TEMPLATE_SKELETONS here rather than granted to
      // the model as a tool. word_template deliberately has no pill_word_copy_text
      // in allowedTools — given one, it stopped calling this tool and pasted an NDA
      // it had written itself. Building the Copy pill from the same constant the
      // Insert pill uses keeps the two byte-identical and leaves the model no way
      // to substitute its own draft. markdownToClipboardHtml turns the '#' markers
      // into real Heading styles on paste.
      {
        type: "office_action",
        label: "Copy",
        handler: "word.copy_text",
        args: { text: markdown },
      },
    ]);
    return { rendered: true, sections };
  }),

  // TO-02 Variant B — ONE button carrying the structured sections.
  //
  // A tool call rather than JSON in the reply text, which is what the
  // specification's prototype used. The prose-payload route was tried first and
  // the model simply did not emit it: the stored reply was 46 characters of
  // confirmation and nothing else. A tool schema is enforced by the tool layer
  // rather than by an instruction the model can quietly skip — the same lesson
  // DP-05's candidates hand-off produced.
  "ui.pill.word.insert_outline": async ({ sections, depth }) => {
    // Two callers, and only one of them authors anything.
    //
    // BUILD MODE passes `sections`: the model invented those headings and stubs,
    // so they can only come from the model. Unchanged.
    //
    // READ MODE passes `depth`: the outline is the document's OWN, already read
    // this turn, and asking the model to retype 171 sections as arguments is what
    // truncated it. The sections are rebuilt here from a fresh read instead —
    // one sweep, guaranteed current, and the payload is one word. Same lesson as
    // insert_template carrying only its key.
    const supplied = (Array.isArray(sections) ? sections : [])
      .map((s) => _s((s as { heading?: unknown } | null)?.heading).trim())
      .filter(Boolean);
    // A rebuild is worth doing when the document has an outline of its own, or
    // when READ MODE asked for one by depth. BUILD MODE on an unstructured
    // document reaches neither and still takes no read.
    const rebuilt =
      documentHasOwnOutline() || (sections === undefined && depth !== undefined)
        ? await outlineFromDocument(depth)
        : null;
    // Prefer the rebuild only when there is one, and when `sections` did not
    // carry an outline the model actually AUTHORED. An empty rebuild means the
    // document has no outline after all, which is the one case a payload is the
    // only outline there is.
    const useRebuilt =
      rebuilt !== null &&
      rebuilt.sections.length > 0 &&
      (supplied.length === 0 || sectionsAreDocumentHeadings(supplied, rebuilt.rows));
    const built = useRebuilt ? rebuilt : null;
    const list = (built?.sections ?? (Array.isArray(sections) ? sections : [])) as Array<Record<string, unknown>>;
    const usable = list
      .filter((s) => _s(s?.heading).trim())
      .map((s) => ({
        heading: _s(s.heading).trim(),
        // Levels arrive as a string from the tool schema ("1") but as a NUMBER when
        // the model copies them off a `toc` entry, and `_s` returns "" for a number —
        // so every numeric level silently collapsed to 1 and a three-level document
        // came back as a flat run of Heading 1s (AC-2).
        level: normaliseOutlineLevel(s.level),
        stub: _s(s.stub),
      }));
    // The schema cannot express "exactly one of sections|depth" — a top-level
    // anyOf in a function's parameters is handled inconsistently by the
    // providers — so the two-mode contract is enforced here instead.
    if (usable.length === 0) {
      throw new Error(
        "pill_word_insert_outline needs either `sections` (an outline you generated) " +
          "or `depth` (to show the document's own outline). Got neither, or an empty outline.",
      );
    }
    emitPills([
      {
        type: "office_action",
        label: `Insert outline (${usable.length})`,
        handler: "word.insert_outline",
        args: { sections: usable },
      },
      // Copy, built from the SAME sections the Insert pill carries.
      //
      // It used to come from a separate pill_word_copy_text call whose text the
      // prompt asked for as "PLAIN TEXT … no #". With no heading markers,
      // markdownToClipboardHtml had nothing to convert and emitted a wall of
      // <p> — so pasting produced an unstyled block while Insert produced real
      // headings, from the same outline. Deriving both from `sections` makes the
      // two routes agree by construction, which is what the specification means
      // by "canned and AI outlines share one insertion routine".
      {
        type: "office_action",
        label: "Copy",
        handler: "word.copy_text",
        args: { text: outlineSectionsToMarkdown(usable) },
      },
    ]);
    return {
      rendered: true,
      sections: usable.length,
      ...(built ? { outline: { rows: built.rows } } : {}),
    };
  },

  "ui.pill.word.apply_review": async ({ changes }) => {
    const rendered = emitChangesPill<{ original: string; replacement: string }>(
      "pill_word_apply_review",
      "word.apply_review",
      "Apply changes",
      changes,
    );
    const originals = (Array.isArray(changes) ? changes : []).map((c) =>
      _s((c as { original?: unknown } | null)?.original),
    );
    const realigned = await realignTurnHighlights(originals);
    return { ...rendered, ...(realigned > 0 ? { realigned } : {}) };
  },

  // RV-01 — the specification's Variant A bar: "Apply review (N) · Copy".
  //
  // Deliberately NOT pill_word_apply_review. That pill's handler suspends
  // tracking and overwrites the flagged text outright; this one routes to
  // executeReviewEdits, which forces Track Changes on and leaves a comment
  // per finding, so the reviewer keeps the audit trail the specification
  // requires (AC-2: a comment AND a tracked change per finding).
  //
  // The payload is richer than apply_review's { original, replacement }: each
  // finding also carries `problem`, which becomes the comment text. A finding
  // with no `suggestion` is comment-only and still counts — that is how a
  // reviewer flags something they do not want silently rewritten.
  "ui.pill.word.apply_review_edits": asPromise(({ findings }) => {
    const list = (Array.isArray(findings) ? findings : []).filter(
      (f): f is ReviewEditFinding => Boolean((f as ReviewEditFinding)?.matchText),
    );
    if (list.length === 0) {
      // NOT a throw. A throw is a failed tool call, and the graph answers a
      // failed call with "Sorry — I couldn't complete that" — which is what
      // /passive returned for text that simply had no passive voice in it
      // (AIVA-7724). The prompt tells the model to call nothing when it has no
      // findings, and it mostly obeys; a prompt rule is only as good as the
      // model's compliance, so the clean case is handled here too, where it
      // cannot be talked out of.
      //
      // `rendered: false` + `message` is the contract `_client_tool_fallback`
      // reads (shared/agent_base.py): a tool that ran fine and had nothing to
      // do supplies the turn's closing line itself. Deliberately not
      // passive-voice wording — /review and the other review skills share this
      // pill and a clean document means the same thing for all of them.
      return {
        rendered: false,
        message: "I checked and found nothing to flag, so there is no review to apply.",
      };
    }
    emitPills([
      {
        type: "office_action",
        // Count in the label so the user sees the blast radius before clicking,
        // exactly as the apply_review pill does.
        label: `Apply review (${list.length})`,
        handler: "word.apply_review_edits",
        args: { findings: list },
      },
    ]);
    return { rendered: true };
  }),

  "ui.pill.word.clear_highlights": asPromise(() => {
    // Claims only the highlights added since the last pill was built, so this
    // pill's own "Clear highlights" click can never remove a different
    // response's highlights (e.g. /risk highlighting the same paragraph
    // /passive already flagged) — see claimSessionHighlights.
    //
    // The LLM sometimes emits the clear pill in turns that never highlighted
    // anything (over-obeying the "clear_highlights is always last" prompt
    // rule). With nothing to clear the button is confusing noise — report
    // success to the graph but render no pill.
    const ids = claimSessionHighlights();
    if (ids.length === 0) {
      // `message` as well as `reason`: this is frequently the LAST call of a
      // clean turn (the model over-obeys "clear_highlights is always last"), and
      // `_client_tool_fallback` reads `message` only. With just a `reason` the
      // turn still ended on the generic apology for a document that was fine
      // (AIVA-7724).
      return {
        rendered: false,
        reason: "no highlights to clear",
        message: "Nothing was highlighted, so there is nothing to clear.",
      };
    }
    emitPills([
      {
        type: "office_action",
        label: "Clear highlights",
        handler: "word.clear_highlights",
        // `turn` is the pill's real claim; `ids` rides along so the same pill
        // still works if it is replayed by an older add-in build.
        args: { ids, turn: currentHighlightTurn() },
      },
    ]);
    return { rendered: true };
  }),

  /**
   * Emit the page-number pill, with the impact line the approval card shows.
   *
   * The survey is a pure read: it reports how many sections there are and what
   * their footers already hold, so the user sees "3 sections; the footer already
   * contains ABC-0250" *before* approving rather than discovering it after.
   * A host that can't enumerate sections degrades to the generic warning.
   */
  "ui.pill.word.page_numbers": async (args) => {
    if (hasBatesIntent(args)) {
      return { rendered: false, reason: "bates request — page numbers refused" };
    }
    const position: PageNumberPosition = _s(args?.position) === "header" ? "header" : "footer";
    const alignment = readPageNumberAlignment(args);
    // Same resolver as the write handler, so the label cannot promise a format,
    // numeral or start that a skip run is unable to deliver.
    const plan = planFrontMatterSkip(
      args,
      _s(args?.format) === "with_total" ? "with_total" : "simple",
      pageNumeralStyle(args),
      pageNumberStartAt(args),
    );
    const { format, numeralStyle, startAt, skipFrontMatter } = plan;
    const base = format === "with_total" ? "Add page X of Y" : "Add page numbers";
    // The numeral and the start number go ON THE BUTTON, not just in chat prose:
    // the button is the last thing the user reads before approving the write,
    // and an unlabelled one is what let "I'm adding Roman numerals" sit above an
    // Arabic stamp (AIVA-7863) — a start of 500 that never reaches the write
    // fails the same way (AIVA-7867).
    const label =
      `${base}${numeralStyleLabelSuffix(numeralStyle)}${pageNumberStartNote(startAt)}` +
      frontMatterLabelSuffix(plan);

    const survey = await surveyWordRegions(position);
    // Fires the approval card's confirmation popup, so the user cannot miss that
    // the region already holds content (letterhead, a legend, other numbering)
    // before the write lands. Nothing is cleared — the field is appended — but
    // the ticket calls for explicit confirmation over existing furniture, and a
    // line on the card was too easy to skim past. Boolean, not a string, so the
    // handler's hasBatesIntent guard (which scans string args) never sees it.
    const hasExistingContent = Boolean(survey && survey.occupied.length > 0);
    // Stated on the card only when it is not the centred default, so the user
    // can see BEFORE clicking that the left/right they asked for was understood.
    const alignPhrase = alignment === "center" ? "" : ` ${alignment}-aligned`;
    // The front-matter sentence goes AFTER the impact line, not inside it: the
    // line's own shape is "Adds <what> to the <region> of N sections", and the
    // skip is a fact about which pages get a number, not about the numbering.
    const impactLine =
      pageNumberImpactLine(
        survey,
        position,
        alignPhrase,
        `${numeralStyleNote(numeralStyle)}${pageNumberStartNote(startAt)}`,
      ) + frontMatterNote(plan);

    emitPills([
      {
        type: "office_action",
        label,
        handler: "word.insert_page_numbers",
        // `alignment` and `numeral_style` must ride on the pill args: the click
        // is a separate turn from the one that chose them, so anything not
        // carried here is lost and the write silently re-centres in Arabic
        // (AIVA-7862, AIVA-7863).
        args: {
          format,
          position,
          alignment,
          numeral_style: numeralStyle,
          // Omitted entirely at the default, so an ordinary "add page numbers"
          // still emits byte-identical pill args and cannot regress.
          ...(startAt > PAGE_NUMBER_START_MIN ? { start_at: startAt } : {}),
          // Rides on the pill args for the same reason alignment does: the
          // click is a separate turn, and a skip that does not travel with it
          // numbers the contents pages after the user approved a button that
          // said they would stay blank.
          ...(skipFrontMatter ? { skip_front_matter: true } : {}),
          impact_line: impactLine,
          ...(hasExistingContent ? { confirm_existing: true } : {}),
        },
      },
    ]);
    return { rendered: true };
  },

  // AIVA-7868 — removal had a working handler and no way to reach it: no tool
  // was registered, so the skill's prompt told the model to acknowledge the
  // request and call nothing. "Remove the page numbers" therefore produced a
  // sentence and an unchanged document.
  "ui.pill.word.remove_page_numbers": async (args) => {
    const position = readRemovalRegion(args);
    const regions = removalRegions(position);
    const label = regionLabel(position);

    // Read EVERY region the click will sweep, so the card's count is the count
    // that will actually go. Surveying only the footer while removing from both
    // would understate the blast radius, and removal is a DELETE on a region
    // that may also hold letterhead or a legend (G-12).
    const surveys = await Promise.all(regions.map((r) => surveyWordRegions(r)));
    const seen = surveys.filter((s): s is NonNullable<typeof s> => s !== null);
    const numbered = seen.reduce(
      (total, s) => total + s.occupied.filter((r) => r.looksBatesStamped !== true).length,
      0,
    );
    const sectionCount = seen.reduce((most, s) => Math.max(most, s.sectionCount), 0);
    const anyBatesStamped = seen.some((s) => s.anyBatesStamped);

    let impactLine: string;
    if (seen.length === 0) {
      impactLine = `Removes the page numbers from the ${label}. Other ${label} content is kept.`;
    } else if (numbered === 0) {
      impactLine =
        `No page numbers found in the ${label} of ${sectionCount} ` +
        `section${sectionCount === 1 ? "" : "s"}. Nothing will be removed.`;
    } else {
      impactLine =
        `Removes the page numbers from the ${label}. Anything else there — letterhead, a ` +
        `confidentiality legend — is KEPT.`;
      if (anyBatesStamped) {
        // A Bates stamp is not ours to delete: removing it silently would
        // corrupt a production bundle (G-14).
        impactLine += " A Bates stamp is also present and will be left alone.";
      }
    }

    emitPills([
      {
        type: "office_action",
        label: "Remove page numbers",
        handler: "word.remove_page_numbers",
        args: { position, impact_line: impactLine },
      },
    ]);
    return { rendered: true };
  },

  "ui.pill.word.insert_table": asPromise(({ headers, rows }) => {
    const h = (Array.isArray(headers) ? headers : []).map(String);
    const r = (Array.isArray(rows) ? rows : []).map((row) =>
      (Array.isArray(row) ? row : []).map(String),
    );
    emitPills([
      { type: "office_action", label: "Insert table here", handler: "word.insert_table", args: { headers: h, rows: r, insert_position: "at_cursor" } },
      { type: "office_action", label: "Add table to end", handler: "word.insert_table", args: { headers: h, rows: r, insert_position: "append_to_end" } },
    ]);
    return { rendered: true };
  }),

  "ui.pill.word.insert_toc": asPromise(({ changes }) => {
    // Optional heading restructure rides along in the pill args so a single
    // click reformats the headings AND inserts the TOC.
    const changeList = (Array.isArray(changes) ? changes : []) as Array<{ text: string; style: string }>;
    emitPills([
      {
        type: "office_action",
        label: "Insert table of contents",
        handler: "word.insert_toc",
        args: changeList.length > 0 ? { changes: changeList } : {},
      },
    ]);
    return { rendered: true };
  }),

  "ui.pill.word.apply_styles": asPromise(({ changes }) =>
    emitChangesPill<{ text: string; style: string }>(
      "pill_word_apply_styles",
      "word.apply_styles",
      "Apply formatting",
      changes,
    ),
  ),

  // The recovery from an ambiguous anchor. Typing the answer does not survive
  // the trip: Word re-routes every turn, so "the third one" is classified with
  // no memory of the question, and it is not text that appears in the document
  // either. One pill per occurrence sidesteps both — the click dispatches
  // straight to the handler with `occurrence` already set.
  "ui.pill.word.choose_comment_occurrence": asPromise(({ phrase, comment, occurrences }) => {
    const p = _s(phrase);
    const c = _s(comment);
    if (!p || !c) throw new Error("pill_word_choose_comment_occurrence requires phrase and comment");

    const choices = (Array.isArray(occurrences) ? occurrences : [])
      .map((raw, i) => {
        const item = (raw ?? {}) as { occurrence?: unknown; label?: unknown };
        const nth = typeof item.occurrence === "number" && item.occurrence > 0 ? item.occurrence : i + 1;
        // A bare ordinal is useless in the margin of a long agreement; the
        // label should name the place, so fall back to one only when the
        // caller gave nothing better.
        return { occurrence: nth, label: _s(item.label) || `Occurrence ${nth}` };
      })
      .filter((choice, i, all) => all.findIndex((o) => o.occurrence === choice.occurrence) === i);

    if (choices.length === 0) {
      throw new Error("pill_word_choose_comment_occurrence requires at least one occurrence");
    }

    emitPills(
      choices.map((choice) => ({
        type: "office_action" as const,
        label: choice.label,
        handler: "word.insert_comment",
        args: { phrase: p, comment: c, occurrence: choice.occurrence },
        // One of N: once the user has answered, the other clauses must go quiet.
        once: true,
      })),
    );
    return { rendered: true, choices: choices.length };
  }),

  "ui.pill.word.insert_comment": asPromise(({ phrase, comment, occurrence }) => {
    const p = _s(phrase);
    const c = _s(comment);
    if (!p || !c) throw new Error("pill_word_insert_comment requires phrase and comment");
    // An anchor the user has already disambiguated stays disambiguated when the
    // pill runs — otherwise the button would come back asking the same question.
    const nth = typeof occurrence === "number" && occurrence > 0 ? { occurrence } : {};
    emitPills([
      {
        type: "office_action",
        label: "Add comment",
        handler: "word.insert_comment",
        args: { phrase: p, comment: c, ...nth },
        // A second click used to add a second identical comment on the same anchor.
        once: true,
      },
    ]);
    return { rendered: true };
  }),

  // DP-05 / G-17 closure. One pill PER CANDIDATE, so nothing is inserted until
  // the user picks a specific message:
  //  - several matches → one pill each, labelled with its subject, which is the
  //    "pickable result cards, insert only on selection" the closure asks for;
  //  - exactly one match → a single pill whose ``impact_line`` names the subject,
  //    sender and date, which is the confirm line before inserting.
  //
  // Both cases route through the same approval card, so the shipping behaviour
  // that inserted the first hit sight-unseen is now unreachable: this emitter
  // cannot write, and the handler it points at cannot search.
  "ui.pill.word.insert_email": asPromise(({ candidates }) => {
    const list = (Array.isArray(candidates) ? candidates : []) as Array<Record<string, unknown>>;
    const usable = list.filter((c) => _s(c.body).trim());
    if (usable.length === 0) throw new Error("pill_word_insert_email requires at least one candidate with a body");

    const many = usable.length > 1;
    // Which labels the DATE alone cannot tell apart (AIVA-7956).
    //
    // The date was added to break subject collisions, but `dateLabel` is a DAY
    // ("05 Jun 2026"), and a thread's replies routinely land on one day. So
    // several distinct messages still produced one identical label, dedupePills
    // keyed on it, and the picker silently rendered 6 pills for 15 candidates
    // while the model — reading `candidates` — correctly said 15. Losing a real
    // message from the picker is the misidentification G-17 exists to prevent,
    // and a count that disagrees with what is on screen is how the user finds
    // out. Anything ambiguous at day precision gets the time as well.
    const dayKey = (c: Record<string, unknown>) =>
      `${_s(c.subject).slice(0, 34)}|${_s(c.dateLabel)}`;
    const ambiguous = new Set(
      usable
        .map(dayKey)
        .filter((k, i, all) => all.indexOf(k) !== i),
    );
    emitPills(
      usable.map((c) => {
        const subject = _s(c.subject) || "(no subject)";
        const sender = _s(c.fromName) || _s(c.fromAddress);
        const day = _s(c.dateLabel);
        // `date` is the full ISO timestamp the card already carries, so the
        // time costs no new payload — it is only spent where the day is not
        // enough, to keep the common label short.
        const time = ambiguous.has(dayKey(c)) ? formatEmailTime(_s(c.date)) : "";
        const dateLabel = time ? `${day} ${time}`.trim() : day;
        const datePart = dateLabel ? ` (${dateLabel})` : "";
        const provenance = [sender, dateLabel].filter(Boolean).join(" · ");
        const provenanceSuffix = provenance ? ` — ${provenance}` : "";
        return {
          type: "office_action" as const,
          // With one candidate the subject is already in the impact line, so the
          // button stays a plain verb. With several, the label IS how the user
          // tells them apart, so it carries the subject — AND the date.
          //
          // The date is not decoration. Threads produce candidates that share a
          // subject exactly ("RE: AiVA Outlook Test" twice, "Financials to
          // review" twice in the AIVA-6957 dev trace), and dedupePills keys on
          // handler + label — so subject-only labels collided and a real,
          // distinct email was silently dropped from the picker. Losing a
          // candidate is precisely the misidentification G-17 exists to prevent.
          // Subject trimmed to 34 so "Insert: " + subject + " (10 Aug 2026)"
          // still fits the task-pane row; the full subject is in the confirm line.
          label: many ? `Insert: ${subject.slice(0, 34)}${datePart}` : "Insert this email",
          handler: "word.insert_email",
          args: {
            preface: _s(c.preface),
            body: _s(c.body),
            subject,
            sender,
            // The message id, carried purely so dedupePills has something
            // GUARANTEED distinct to key on (AIVA-7956). The label above is
            // built to be unique and normally is, but it is a display string —
            // truncated to 34 characters and rounded to the minute — so it can
            // never be the identity of a candidate. Two same-minute replies with
            // the same 34-character prefix are rare, not impossible, and the
            // failure mode is a message vanishing from the picker.
            email_id: _s(c.id),
            // Embedded images must be read from the mailbox the message was listed from.
            ...(_s(c.mailbox) ? { mailbox: _s(c.mailbox) } : {}),
            // Rendered verbatim by WordPillApprovalCard — the user reads which
            // message is about to land in their document before clicking.
            impact_line: `Inserting “${subject}”${provenanceSuffix} at the cursor.`,
          },
        };
      }),
    );
    return { rendered: true, candidates: usable.length };
  }),

  "ui.pill.word.replace_all": asPromise(({ find, replace, match_case }) => {
    const f = _s(find);
    const r = _s(replace);
    if (!f) throw new Error("pill_word_replace_all requires find");
    const matchCase = match_case === true;
    emitPills([
      {
        type: "office_action",
        label: "Replace all",
        handler: "word.replace_all",
        args: { find: f, replace: r, ...(matchCase ? { match_case: true } : {}) },
      },
    ]);
    return { rendered: true };
  }),

  /**
   * Emit the remove-Bates pill (AC-4).
   *
   * Removal is destructive in a production sense — it strips the citation
   * numbers off a bundle — so it goes through a pill like every other write.
   * With no prefix from the user, EVERY Bates stamp goes, whatever its prefix
   * (AC-4's removal clause has no prefix language; defaulting to WL- silently
   * missed SIM-/ABC- series), and the impact line names the stamps the survey
   * actually FOUND so the user can see which numbers are about to go.
   */
  "ui.pill.word.remove_bates": async (args) => {
    const prefix = readBatesRemovalPrefix(args);
    const position = readRemovalRegion(args);
    const regions = removalRegions(position);
    const where = position ?? "footer and header";

    const { sectionCount, stamps } = await surveyBatesRemovalTargets(regions, prefix);

    const what = prefix ? `the "${prefix}…" stamps` : "every Bates stamp, whatever its prefix,";
    let across = "every section";
    if (sectionCount != null) {
      across = `${sectionCount} section${sectionCount === 1 ? "" : "s"}`;
    }
    let impactLine =
      `Removes ${what} from the ${where} of ${across}. ` +
      `Anything else there — letterhead, a confidentiality legend, a page number — is left alone.`;
    if (stamps.length > 0) {
      impactLine += ` Found: ${stamps.slice(0, 4).join(", ")}${stamps.length > 4 ? ", …" : ""}.`;
    }

    emitPills([
      {
        type: "office_action",
        label: prefix ? `Remove ${prefix} numbering` : "Remove Bates numbering",
        handler: "word.remove_bates",
        args: {
          ...(prefix ? { prefix } : {}),
          ...(position ? { position } : {}),
          impact_line: impactLine,
        },
      },
    ]);
    return { rendered: true };
  },

  /**
   * Emit the Bates pill, with the impact line the approval card shows.
   *
   * The survey is a pure read of every section's footer, so the confirmation can
   * name what is already there — a confidentiality legend, an existing stamp —
   * BEFORE the user approves (the specification's G-12 closure). It also names
   * the first stamp, because "ABC-0250 onwards" is the fact a litigation user
   * actually needs to check.
   */
  "ui.pill.word.bates": async (args) => {
    const config = normaliseBatesConfig(args ?? {});
    const stamp = formatBatesStamp(config);
    const { position } = config;

    const placement = describeBatesPlacement(config);
    const survey = await surveyWordRegions(position);
    // Fires the approval card's confirmation popup when the region already holds
    // content — see the page-numbers emitter for the full reasoning.
    const hasExistingContent = Boolean(survey && survey.occupied.length > 0);
    let impactLine = `Stamps ${stamp} onwards in the ${position} (${placement}) of every section.`;
    if (survey) {
      const s = survey.sectionCount;
      impactLine = `Stamps ${stamp} onwards in the ${position} (${placement}) of ${s} section${s === 1 ? "" : "s"}.`;
      if (survey.occupied.length > 0) {
        const found = survey.occupied
          .slice(0, 3)
          .map((r) => `section ${r.section}: "${changeSnippet(r.text)}"`)
          .join("; ");
        impactLine +=
          ` The ${position} already contains content — ${found}. It will be kept; the stamp is added below it.`;
        if (survey.anyBatesStamped) {
          // A prior stamp of any prefix is replaced by the new series
          // (insertWordBates deletes any Bates-looking paragraph before writing),
          // not added alongside — so the warning says it goes, rather than telling
          // the user to remove it, which would leave them expecting two numbers.
          // A page number sharing the footer is left alone: a bundle keeps both.
          impactLine +=
            " One of those already looks like a Bates stamp — it will be replaced by the new series, not added" +
            " alongside, so the pages won't carry two numbers.";
        }
      }
    }
    if (config.startAt > 1) {
      // G-13/AC-6: the offset write is verify-or-fall-back (see
      // insertWordBatesOffset), so the card says so BEFORE approval — the one
      // thing that must never happen is a promised offset silently no-opping.
      impactLine +=
        ` The start offset needs Word's calculated field — the result is checked after writing, and if this` +
        ` version of Word can't render it the stamp is removed and you'll be told, so field code never reaches` +
        ` the ${position}.`;
    }

    emitPills([
      {
        type: "office_action",
        label: `Apply Bates numbering (${stamp})`,
        handler: "word.insert_bates",
        args: {
          prefix: config.prefix,
          padding: config.padding,
          start_at: config.startAt,
          position,
          alignment: config.alignment,
          impact_line: impactLine,
          ...(hasExistingContent ? { confirm_existing: true } : {}),
        },
      },
    ]);
    return { rendered: true };
  },


  "ui.pill.word.insert_hyperlink": asPromise(({ url, text }) => {
    const raw = _s(url);
    if (!raw) throw new Error("pill_word_insert_hyperlink requires url");
    // Refuse at emit time, not on click: a pill the user cannot safely press
    // should never render. Keeps the refusal in the turn the model can explain.
    const safeUrl = normaliseUrl(raw);
    if (!safeUrl) {
      return { rendered: false, reason: `refused unsupported URL scheme: ${raw.slice(0, 40)}` };
    }
    const label = _s(text).trim();
    emitPills([
      {
        type: "office_action",
        label: "Insert link",
        handler: "word.insert_hyperlink",
        args: { url: safeUrl, ...(label ? { text: label } : {}) },
      },
    ]);
    return { rendered: true };
  }),



  // ---------------------------------------------------------------------------
  // Word copy pill (pill_word_copy_text) — Word-only, independent of the
  // Outlook clipboard pill. Used by /template: copy the generated template to
  // the clipboard so it can be pasted into another document.
  // ---------------------------------------------------------------------------

  "word.copy_text": async (args) => {
    const text = _s(args?.text);
    if (!text) throw new Error("word.copy_text called with empty text");
    // Prefer the shared renderer, so the clipboard carries the document's font
    // and the same block structure an Insert pill would have written
    // (AIVA-7074). markdownToClipboardHtml stays as the fallback for text that
    // parses to no blocks at all, so the content still reaches the clipboard.
    const html = (await buildWordClipboardHtml(text)) || markdownToClipboardHtml(text);
    // Prefer a rich write (text/html + text/plain) so pasting into Word gives
    // real Heading styles and bullets instead of literal '#' / '-' markers.
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
        emitToast({ title: "Copied", intent: "success" });
        return { copied: true };
      } catch {
        // Permissions policy / NotAllowedError — fall through.
      }
    }
    if (execCommandCopyRichFallback(html)) {
      emitToast({ title: "Copied", intent: "success" });
      return { copied: true };
    }
    // Plain-text last resorts — content still arrives, just unstyled.
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        emitToast({ title: "Copied", intent: "success" });
        return { copied: true };
      } catch {
        // fall through to the textarea fallback
      }
    }
    if (execCommandCopyFallback(text)) {
      emitToast({ title: "Copied", intent: "success" });
      return { copied: true };
    }
    emitToast({ title: "Couldn't copy. Try again.", intent: "error" });
    throw new Error("Clipboard API not available in this host");
  },

  "ui.pill.word.copy_text": asPromise(({ text }) => {
    const t = _s(text);
    if (!t) throw new Error("pill_word_copy_text requires non-empty text");

    // TO-02 Variant B, recovered from the copy text.
    //
    // The outline skill is supposed to call pill_word_insert_outline with the
    // structure. Across three prompt revisions it never did — it wrote a
    // sentence and called THIS tool, whose text carried the whole outline. The
    // structure was always present; only the hand-off was unreliable. So the
    // Insert button is derived from the text rather than waiting for a call the
    // model does not make.
    //
    // Gated on the text actually parsing as an outline (3+ numbered headings),
    // so an ordinary Copy on a prose reply is untouched.
    const sections = parseOutlineTextToSections(t);
    if (sections.length > 0) {
      const count = sections.length;
      emitPills([
        {
          type: "office_action",
          label: `Insert outline (${count})`,
          handler: "word.insert_outline",
          args: { sections },
        },
        { type: "office_action", label: "Copy", handler: "word.copy_text", args: { text: t } },
      ]);
      return { rendered: true, sections: count };
    }

    emitPills([{ type: "office_action", label: "Copy", handler: "word.copy_text", args: { text: t } }]);
    return { rendered: true };
  }),

  // --- Cross-reference & defined-terms document review (AIVA-7506) ---
  // `word.docreview.scan` and `word.docreview.anchor_findings` are dispatched
  // by two hand-written backend tools (not generic ToolRegistry frontend
  // tools) that call interrupt() directly — see
  // integrations/word/docreview_tools.py. Everything else (the per-rule
  // find/fix pairs) is an ordinary registered frontend tool.
  "word.docreview.scan": async (args, ctx?: HandlerContext) => {
    // AIVA-7516: every scan supersedes whichever came before — see the
    // latestDocReviewScanAttempt comment above. This must be the FIRST
    // statement in the handler so an earlier in-flight scan is superseded
    // the instant a new one starts, not once it happens to reach the persist
    // step.
    const myAttempt = ++latestDocReviewScanAttempt;
    const scanStarted = performance.now();

    const readOnlyRefusal = await docReviewReadOnlyRefusal();
    if (readOnlyRefusal) return readOnlyRefusal;

    const sortChoice = docReviewSortChoice(args);
    if (sortChoice) return sortChoice;

    // Guard against a content control orphaned by an earlier aborted/failed
    // review session — Word's insertContentControl throws GeneralException
    // when the range it's given already sits inside an existing content
    // control, so a stale AIVA_* anchor left over from a previous scan
    // (e.g. one that hit an error partway through anchoring) would make
    // EVERY future scan's anchor step fail at the exact same spot, forever,
    // until cleared.
    await cleanupAllAnchors();

    // Both sweep batches live in their own functions: each is a wall of
    // `wants(...) ? sweep() : empty` ternaries whose only job is to honour
    // the rules hint, and inlining them put this handler over the cognitive
    // complexity cap for reasons that have nothing to do with what it does.
    const wants = buildRulesHintPredicate(args);
    const { brokenReference, bracketHeadingMismatch, exhibitSchedule, definedTermSectionMismatch, shortenedHyperlinkText, definedNotUsed, alphabeticalOrder, missingDefinedTerm, usedBeforeDefined } = await runIndependentSweeps(wants);
    throwIfScanCancelled(ctx);

    // Reuses brokenReference's already-swept references — see findInconsistentFormatting's
    // docstring. Without this, inconsistent_formatting silently re-ran the
    // entire broken_reference paragraph sweep a second time, on top of the
    // round trip broken_reference already cost.
    const { inconsistentFormatting, topicMismatch, missingHyperlink, usedNotDefined, hyperlinkTarget } = await runDependentSweeps(wants, brokenReference);
    throwIfScanCancelled(ctx);
    // AIVA-8315: the per-sweep lines above say WHICH sweep was slow; this says
    // how much of the time limit the sweeps used before evaluation and persist.
    console.info(`[docReview] scan: all sweeps done after ${Math.round(performance.now() - scanStarted)}ms`);

    // AIVA-7506 frontend rework: evaluate the 10 rules that need no LLM
    // judgment right here, and persist them directly — never through the
    // LLM. numbering_order needs no sweep of its own; it evaluates from brokenReference's headings.
    //
    // Persists whatever was actually swept above — a rule skipped by the
    // rules hint naturally produces zero findings here, no separate filter
    // needed. word_docreview_judge (backend) is still where a scoped check
    // is actually ENFORCED, by pruning whatever ends up persisted (from
    // here, or from a skipped-hint full sweep) down to just what the user
    // asked about. See docreview_tools.py's _judge/_prune_out_of_scope_findings:
    // a real test showed the model does not reliably pass this handler a
    // rules hint, so correctness can never depend on it — skipping a sweep
    // above is purely a speed optimization, safe to get wrong or omit.
    const deterministicFindings: FindingCreate[] = [
      ...evaluateMissingHyperlink(missingHyperlink),
      ...evaluateBrokenReference(brokenReference),
      ...evaluateAmbiguousReference(brokenReference),
      ...evaluateBracketHeadingMismatch(bracketHeadingMismatch),
      ...evaluateInconsistentFormatting(inconsistentFormatting),
      ...evaluateExhibitScheduleMismatch(exhibitSchedule),
      ...evaluateShortenedHyperlinkText(shortenedHyperlinkText),
      ...evaluateDefinedNotUsed(definedNotUsed),
      ...evaluateAlphabeticalOrder(alphabeticalOrder, parseDefinitionSortKey(args?.sortKey)),
      // numberedItems, not headings: a heading list carries only numbers that
      // are literal text, which is blind to an auto-numbered document
      // (AIVA-8297). `?? []` covers a sweep that was skipped by the rules hint.
      ...evaluateNumberingOrder(brokenReference.numberedItems ?? []),
      ...evaluateNumberingOrderHeadingAnomalies(brokenReference.headings),
      ...evaluateMissingDefinedTerm(missingDefinedTerm),
      ...evaluateUsedBeforeDefined(usedBeforeDefined),
    ];
    const threadId = ctx?.threadId ?? null;
    // AIVA-7516: only the MOST RECENT scan attempt may persist. If a newer
    // scan has started since this one began, this one lost the client-side
    // timeout race a while ago and the chat turn has already moved on —
    // persisting now would create a duplicate-id copy of every finding the
    // newer scan is about to write.
    const isStillCurrent = myAttempt === latestDocReviewScanAttempt;
    if (threadId && isStillCurrent) {
      throwIfScanCancelled(ctx);
      // AIVA-9191: what this scan could not check, shown under the review.
      setScanNotes(threadId, definedTermSectionNotes(definedTermSectionMismatch));
      // AIVA-8280: before the cycle's findings are written, tell the server
      // which comments are still in the document, so a comment fix the
      // operator has since undone is reopened rather than treated as a live
      // decision. Must come FIRST — bulkCreateFindings is what reads those
      // statuses to decide what not to re-flag.
      await reconcileCommentFixes(threadId, ctx);
      // AIVA-8538: runs AFTER reconcile deliberately. Reconcile is what
      // reopens a fix whose comment the operator has since deleted; a deleted
      // comment is then absent from this read too, so an undone fix is still
      // re-offered rather than silently suppressed.
      const toPersist = await withoutAlreadyCommentedFindings(deterministicFindings);
      // AIVA-8199: the last check before the write — both awaits above can be slow.
      throwIfScanCancelled(ctx);
      await bulkCreateFindings(threadId, toPersist, ctx?.apiToken ?? null, ctx?.refresh);
    }

    // Only the judgment-relevant subset crosses to the LLM — narrowed the
    // same way rules.py/judgment.py's own pre-filters and bucketing used to
    // do server-side (see filterTopicMismatchJudgmentCandidates/filterHyperlinkTargetMismatchJudgmentTargets/
    // findExhibitScheduleAmbiguousMentions), so word_docreview_judge's schema and this
    // return payload stay in lockstep. Keys must match docreview_tools.py's
    // _judge() reads of scan_result (topic_mismatch/exhibit_schedule_ambiguous/
    // hyperlink_target_mismatch/used_not_defined) — the AIVA-8087 rename missed
    // this return object, so every _judge lookup silently missed and every
    // judgment rule evaluated zero candidates (AIVA-8199 follow-up).
    return {
      topic_mismatch: filterTopicMismatchJudgmentCandidates(topicMismatch),
      // AIVA-8543: meaningCandidates ride the same routing key, so a full or an
      // Exhibit/Schedule-scoped check judges them with no new rule key.
      exhibit_schedule_ambiguous: {
        mentions: findExhibitScheduleAmbiguousMentions(exhibitSchedule),
        meaningCandidates: findExhibitScheduleMeaningCandidates(exhibitSchedule),
      },
      hyperlink_target_mismatch: filterHyperlinkTargetMismatchJudgmentTargets(hyperlinkTarget),
      used_not_defined: usedNotDefined,
      // AIVA-8297 follow-up: judged, not evaluated here — the sweep already
      // ruled out every pair a fact settles, so only the question of what a
      // sentence claims is left for the LLM.
      defined_term_section_mismatch: definedTermSectionMismatch,
      headings: brokenReference.headings,
      ...(wants("ALPHABETICAL_ORDER") ? { sort_order: sortOrderSummary(args, alphabeticalOrder) } : {}),
    };
  },

  // Also registered standalone (ToolRegistry: word_docreview_find_broken_reference)
  // so a future conversational "just re-check my broken references" ask can
  // call it directly — not on the primary scan-then-anchor path, which
  // calls findBrokenReference in-process above.
  "word.docreview.find_missing_hyperlink": async () => findMissingHyperlink(),
  "word.docreview.find_broken_reference": async () => {
    // numberedItems is dropped on the way out: it exists only for
    // numbering_order, which runs in-process on the scan path above, and it
    // carries the full text of every numbered list paragraph in the document.
    // This return value goes straight to the model, so shipping it here would
    // be pure token cost on a tool that never uses it.
    const { numberedItems: _numberedItems, ...sweep } = await findBrokenReference();
    return sweep;
  },
  "word.docreview.find_bracket_heading_mismatch": async () => findBracketHeadingMismatch(),
  "word.docreview.find_topic_mismatch": async () => findTopicMismatchData(),
  "word.docreview.find_inconsistent_formatting": async () => findInconsistentFormatting(),
  "word.docreview.find_exhibit_schedule_mismatch": async () => findExhibitScheduleMismatch(),
  "word.docreview.find_defined_term_section_mismatch": async () => findDefinedTermSectionMismatch(),
  "word.docreview.find_hyperlink_target_mismatch": async () => findHyperlinkTargetMismatchData(),
  "word.docreview.find_shortened_hyperlink_text": async () => findShortenedHyperlinkText({ shortenedOnly: false }),
  "word.docreview.find_defined_not_used": async () => findDefinedNotUsed(),
  "word.docreview.find_used_not_defined": async () => findUsedNotDefinedCandidates(),
  "word.docreview.find_alphabetical_order": async () => findAlphabeticalOrder(),
  "word.docreview.find_missing_defined_term": async () => findMissingDefinedTerm(),
  "word.docreview.find_used_before_defined": async () => findUsedBeforeDefined(),

  "word.docreview.anchor_findings": async (args) => {
    const findings = Array.isArray(args?.findings)
      ? (args.findings as Array<{ index: number; matchText: string; occurrenceIndex?: number }>)
      : [];
    const anchored = await anchorFindings(findings);
    return { anchored };
  },

  "word.docreview.fix_missing_hyperlink": async (args) => {
    const { anchorTag, expected, targetHeadingText, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      targetHeadingText?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixMissingHyperlink(anchorTag, expected, targetHeadingText ?? "", commentText);
  },

  "word.docreview.fix_broken_reference": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixBrokenReference(anchorTag, expected, commentText);
  },

  "word.docreview.fix_bracket_heading_mismatch": async (args) => {
    const { anchorTag, expected, correctFullText, referenceText, headingText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      correctFullText?: string;
      referenceText?: string;
      headingText?: string;
    };
    if (anchorTag == null || expected == null || correctFullText == null) {
      return { applied: false, reason: "missing_args" };
    }
    // AIVA-8745: a finding naming the reference and its heading also links
    // one to the other; an older build's finding only corrects the bracket.
    const link = referenceText && headingText ? { referenceText, headingText } : undefined;
    return fixBracketHeadingMismatch(anchorTag, expected, correctFullText, link);
  },

  "word.docreview.fix_topic_mismatch": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixTopicMismatch(anchorTag, expected, commentText);
  },

  "word.docreview.fix_inconsistent_formatting": async (args) => {
    const { anchorTag, expected, targetUnderline } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      targetUnderline?: string;
    };
    if (anchorTag == null || expected == null || targetUnderline == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixInconsistentFormatting(anchorTag, expected, targetUnderline);
  },

  "word.docreview.fix_exhibit_schedule_mismatch": async (args) => {
    const { anchorTag, expected, correctFullText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      correctFullText?: string;
    };
    if (anchorTag == null || expected == null || correctFullText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixExhibitScheduleMismatch(anchorTag, expected, correctFullText);
  },

  "word.docreview.fix_exhibit_schedule_mismatch_flag": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixExhibitScheduleMismatchFlag(anchorTag, expected, commentText);
  },

  "word.docreview.fix_defined_term_section_mismatch": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixDefinedTermSectionMismatch(anchorTag, expected, commentText);
  },

  "word.docreview.fix_hyperlink_target_mismatch": async (args) => {
    const { anchorTag, expected, targetHeadingText, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      targetHeadingText?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixHyperlinkTargetMismatch(anchorTag, expected, targetHeadingText ?? "", commentText);
  },

  "word.docreview.fix_hyperlink_target_mismatch_flag": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixHyperlinkTargetMismatchFlag(anchorTag, expected, commentText);
  },

  "word.docreview.fix_shortened_hyperlink_text": async (args) => {
    const { anchorTag, expected, correctFullText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      correctFullText?: string;
    };
    if (anchorTag == null || expected == null || correctFullText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixShortenedHyperlinkText(anchorTag, expected, correctFullText);
  },

  "word.docreview.fix_shortened_hyperlink_text_flag": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixShortenedHyperlinkTextFlag(anchorTag, expected, commentText);
  },

  "word.docreview.fix_defined_not_used": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixDefinedNotUsed(anchorTag, expected, commentText);
  },

  "word.docreview.fix_used_not_defined": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixUsedNotDefined(anchorTag, expected, commentText);
  },

  "word.docreview.fix_used_before_defined": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixUsedBeforeDefined(anchorTag, expected, commentText);
  },

  "word.docreview.fix_numbering_order": async (args) => {
    const { anchorTag, expected, correctFullText, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      correctFullText?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || correctFullText == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixNumberingOrder(anchorTag, expected, correctFullText, commentText);
  },

  "word.docreview.fix_numbering_order_relist": async (args) => {
    const { anchorTag, expected, targetListId, listLevel, problem, correctedNumber } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      targetListId?: number | string | null;
      listLevel?: number | null;
      problem?: string;
      correctedNumber?: string;
    };
    if (anchorTag == null || expected == null || problem == null || correctedNumber == null) {
      return { applied: false, reason: "missing_args" };
    }
    // targetListId/listLevel are legitimately absent — the fix then applies the
    // comment alone. `Number(x) || null` would be wrong here: list id 0 is a
    // real id and must not collapse to "no list".
    const parsedListId = targetListId == null ? Number.NaN : Number(targetListId);
    return fixNumberingOrderRelist(
      anchorTag,
      expected,
      Number.isFinite(parsedListId) ? parsedListId : null,
      listLevel ?? null,
      problem,
      correctedNumber,
    );
  },

  "word.docreview.fix_numbering_order_flag": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixNumberingOrderHeadingAnomalyFlag(anchorTag, expected, commentText);
  },

  "word.docreview.fix_alphabetical_order": async (args) => {
    const { anchorTag, expected, term, sortKey } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      term?: string;
      sortKey?: unknown;
    };
    if (anchorTag == null || expected == null || term == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixAlphabeticalOrder(anchorTag, expected, term, parseDefinitionSortKey(sortKey));
  },

  "word.docreview.fix_ambiguous_reference": async (args) => {
    const { anchorTag, expected, commentText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    return fixAmbiguousReference(anchorTag, expected, commentText);
  },

  "word.docreview.fix_missing_defined_term": async (args) => {
    const { anchorTag, expected, commentText, term, sectionLabel, headingText } = (args ?? {}) as {
      anchorTag?: string;
      expected?: string;
      commentText?: string;
      term?: string;
      sectionLabel?: string;
      headingText?: string;
    };
    if (anchorTag == null || expected == null || commentText == null) {
      return { applied: false, reason: "missing_args" };
    }
    // AIVA-8748: a finding that names the term and its section also adds the
    // term to the Definitions section; one without (an older build's, or a
    // term under no heading) comments only, as before.
    const reference = term && sectionLabel ? { term, sectionLabel, headingText: headingText ?? "" } : undefined;
    return fixMissingDefinedTerm(anchorTag, expected, commentText, reference);
  },

  "word.docreview.read_anchor_state": async (args) => {
    const { anchorTag } = (args ?? {}) as { anchorTag?: string };
    if (!anchorTag) return { state: null };
    return { state: await readAnchorState(anchorTag) };
  },

  // AIVA-8209: `anchorTag` alone is no longer enough to say where a finding
  // is — it names a content control the document may no longer have. The
  // finding's own matchText/occurrenceIndex ride along as the fallback, and
  // `expected` only so the caller can be told the text has since moved.
  // Nothing here writes, so a missing anchor is now a recoverable read
  // rather than a dead click.
  "word.docreview.select_anchor": async (args) => {
    const { anchorTag, matchText, occurrenceIndex, expected } = (args ?? {}) as {
      anchorTag?: string;
      matchText?: string;
      occurrenceIndex?: number;
      expected?: string;
    };
    if (!anchorTag && !matchText) return { selected: false };
    return selectFindingLocation(anchorTag ?? null, { matchText, occurrenceIndex, expected });
  },

  // AIVA-8209 Layer 2: the write-side counterpart of `select_anchor`. Called
  // only after a fix has already reported that it has no usable location
  // (`missing_args`/`anchor_missing`) — it re-establishes the anchor from
  // the finding's own text so the fix can be retried, instead of the
  // finding being marked permanently stale over a content control the
  // document happens to have lost.
  "word.docreview.repair_anchor": async (args) => {
    const { matchText, occurrenceIndex } = (args ?? {}) as { matchText?: string; occurrenceIndex?: number };
    if (!matchText) return { anchorTag: null, fingerprint: null };
    return repairFindingAnchor(matchText, occurrenceIndex ?? 0);
  },

  "word.docreview.cleanup_anchors": async () => {
    const removed = await cleanupAllAnchors();
    return { removed };
  },
};
